import { HAZARD_STATEMENT_GROUPS, SECURITY_NOTICE_GROUPS } from 'generated/productRequirements/clpPhrases';
import { equals, map, isEmpty } from 'ramda';
import { isDefined } from '../utils/helpers';

import { IExcelHeader } from 'models/excel';
import IProduct, {
  IFoodlaCategory,
  NutritionalUnit,
  EggWeightClassEnum,
  RecyclingTypeEnum,
  PreparedFormEnum,
  AdditionalCharacteristicsEnum,
  AnimalCategoriesForFeedEnum,
  CategoryWeightlossEnum,
  ApprovalDietFeedEnum,
  ComplianceMetalsSkinContactEnum,
  ConfirmGuidanceSwedishElectronicsEnum,
  QrNutritionClaimsEnum,
  QrHealthClaimsEnum,
  QrElectronicEnum,
  ComplianceToyDirectiveEnum,
  ComplianceBiocidEnum,
  ComplianceBiocidalTreatedEnum,
  ComplianceSunscreenRegulationEnum,
  SunProtectionFactorEnum,
  FishCatchMethodEnum,
  FishProductionMethodEnum,
  CatchAreaEnum,
  SignalWordsEnum,
  HazardStatementsEnum,
  AnimalFeedTypeEnum,
  SalesTypeEnum,
  SecurityNoticesEnum,
  IroningEnum,
  IroningDisplayEnum,
  BatteryEnum,
  FlammableSubtypeEnum,
  BatteryTypeEnum,
  BatteryTechnologyEnum,
  BatteryHazardousMetalEnum,
} from 'models/product';
import { IFoodlaCategoryOption } from 'models/category';
import { isNewFood, isNewNonFood } from './product/ProductUpdateForm/fields/category';

export const ORDER_STATUS_CREATED = 'CREATED';
export const ORDER_STATUS_NOTIFIED_DONE = 'NOTIFIED';
export const ORDER_STATUS_NOTIFIED_FAILURE = 'NOTIFY_FAILURE';
export const ORDER_STATUS_NOTIFIED_ERROR = 'NOTIFY_ERROR';
export const ORDER_STATUS_NOTIFIED_NOTIFYING = 'NOTIFYING';

export const ORDER_STATUS_CREATED_FRONTEND = 'Created';
export const ORDER_STATUS_DONE_FRONTEND = 'Done';
export const ORDER_STATUS_SENDING_FRONTEND = 'Sending';
export const ORDER_STATUS_ERROR_FRONTEND = 'Error';
export const ORDER_STATUS_FRONTEND = [
  ORDER_STATUS_CREATED_FRONTEND,
  ORDER_STATUS_SENDING_FRONTEND,
  ORDER_STATUS_DONE_FRONTEND,
];

export enum STATUS_FILTER {
  ALL = 'ALL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  UNDER_REVIEW = 'UNDER_REVIEW',
  DRAFT = 'DRAFT',
  INACTIVE = 'INACTIVE',
}

export const SUPERUSER_APPROVAL = {
  ADMIN_APPROVED: 'ADMIN_APPROVED',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
  ADMIN_DISAPPROVED: 'ADMIN_DISAPPROVED',
};

export const PRODUCT_STATUS = {
  MARKED_FOR_REMOVAL: 'MARKED_FOR_REMOVAL',
  DELETED: 'DELETED',
};

export const APPLICATION_ROLES = {
  PRODUCER: 'producer',
  LOGISTICIAN: 'logistician',
  ADMIN: 'admin',
  SUPERUSER: 'superuser',
  UNAUTHORIZED: 'unauthorized',
  QUALITY: 'quality',
  STORE: 'store',
};

export const ROOT_CATEGORY_ID = 'root';
export const FOOD_CATEGORY_ID = 'food';
export const NON_FOOD_CATEGORY_ID = 'non-food';

export const FOOD_CATEGORY_LIST = [
  { id: `${FOOD_CATEGORY_ID}-1`, name: 'Mjölkprodukter och motsvarande icke mjölkbaserade produkter' },
  { id: `${FOOD_CATEGORY_ID}-2`, name: 'Fetter och oljor samt fett- och oljeemulsioner' },
  { id: `${FOOD_CATEGORY_ID}-3`, name: 'Glassvaror' },
  { id: `${FOOD_CATEGORY_ID}-4`, name: 'Frukter, bär och grönsaker' },
  { id: `${FOOD_CATEGORY_ID}-5`, name: 'Konfektyrvaror' },
  { id: `${FOOD_CATEGORY_ID}-6`, name: 'Spannmål och spannmålsprodukter' },
  { id: `${FOOD_CATEGORY_ID}-7`, name: 'Bageriprodukter' },
  {
    id: `${FOOD_CATEGORY_ID}-8`,
    name: 'Kött',
    children: [
      {
        id: `${FOOD_CATEGORY_ID}-8-1`,
        name: 'Nötkött',
        children: [
          { id: `${FOOD_CATEGORY_ID}-8-1-1`, name: 'Nötkött Chuck' },
          { id: `${FOOD_CATEGORY_ID}-8-1-2`, name: 'Oxbringa' },
          { id: `${FOOD_CATEGORY_ID}-8-1-3`, name: 'Nötkött tallrik' },
        ],
      },
      { id: `${FOOD_CATEGORY_ID}-8-2`, name: 'Fläsk' },
      { id: `${FOOD_CATEGORY_ID}-8-3`, name: 'Kyckling' },
      { id: `${FOOD_CATEGORY_ID}-8-4`, name: 'Getkött' },
    ],
  },
  { id: `${FOOD_CATEGORY_ID}-9`, name: 'Fisk och fiskeriprodukter' },
  { id: `${FOOD_CATEGORY_ID}-10`, name: 'Ägg och äggprodukter' },
  { id: `${FOOD_CATEGORY_ID}-11`, name: 'Sockerarter, olika former av sirap, honung och bordssötningsmedel' },
  { id: `${FOOD_CATEGORY_ID}-12`, name: 'Salt, kryddor, soppor, såser, sallader och proteinprodukter' },
  {
    id: `${FOOD_CATEGORY_ID}-13`,
    name: 'Livsmedel för särskilda näringsändamål enligt definitionen i direktiv 2009/39/ EG',
  },
  {
    id: `${FOOD_CATEGORY_ID}-14`,
    name: 'Drycker',
    children: [
      {
        id: `${FOOD_CATEGORY_ID}-14-1`,
        name: 'Mjölk',
        children: [
          { id: `${FOOD_CATEGORY_ID}-14-1-1`, name: 'Mjölkkor' },
          { id: `${FOOD_CATEGORY_ID}-14-1-2`, name: 'Getmjölk' },
          { id: `${FOOD_CATEGORY_ID}-14-1-3`, name: 'Sojamjölk' },
        ],
      },
      {
        id: `${FOOD_CATEGORY_ID}-14-2`,
        name: 'Kaffe',
        children: [
          {
            id: `${FOOD_CATEGORY_ID}-14-2-1`,
            name: 'Arabica',
            children: [
              { id: `${FOOD_CATEGORY_ID}-14-2-1-1`, name: 'Typica' },
              { id: `${FOOD_CATEGORY_ID}-14-2-1-2`, name: 'Bourbon' },
              { id: `${FOOD_CATEGORY_ID}-14-2-1-3`, name: 'Caturra' },
              { id: `${FOOD_CATEGORY_ID}-14-2-1-4`, name: 'Catimor' },
              { id: `${FOOD_CATEGORY_ID}-14-2-1-5`, name: 'Jackson' },
              { id: `${FOOD_CATEGORY_ID}-14-2-1-6`, name: 'Kona' },
              { id: `${FOOD_CATEGORY_ID}-14-2-1-7`, name: 'Pacamara' },
              { id: `${FOOD_CATEGORY_ID}-14-2-1-8`, name: 'Villabos' },
            ],
          },
          { id: `${FOOD_CATEGORY_ID}-14-2-2`, name: 'Robusta' },
          { id: `${FOOD_CATEGORY_ID}-14-2-3`, name: 'Espresso' },
          { id: `${FOOD_CATEGORY_ID}-14-2-4`, name: 'Cappuccino' },
          { id: `${FOOD_CATEGORY_ID}-14-2-5`, name: 'Latte' },
        ],
      },
      { id: `${FOOD_CATEGORY_ID}-14-3`, name: 'Te' },
      {
        id: `${FOOD_CATEGORY_ID}-14-4`,
        name: 'Juice',
        children: [
          { id: `${FOOD_CATEGORY_ID}-14-4-1`, name: 'Apelsinjuice' },
          { id: `${FOOD_CATEGORY_ID}-14-4-2`, name: 'Appeljuice' },
        ],
      },
    ],
  },
  { id: `${FOOD_CATEGORY_ID}-15`, name: 'Konsumtionsfärdiga snacks' },
  { id: `${FOOD_CATEGORY_ID}-16`, name: 'Desserter, utom produkter som omfattas av kategorierna 1, 3 och 4' },
  { id: `${FOOD_CATEGORY_ID}-17`, name: 'Kosttillskott enligt definitionen i direktiv 2002/46/EG' },
  {
    id: `${FOOD_CATEGORY_ID}-18`,
    name: 'Bearbetade livsmedel som inte omfattas av kategorierna 1–17, utom livsmedel för spädbarn och småbarn',
  },
];

export const NON_FOOD_CATEGORY_LIST = [{ id: `${NON_FOOD_CATEGORY_ID}-1`, name: 'Ej Livsmedel' }];

export const PRODUCT_CATEGORY_ROOT: IFoodlaCategoryOption = {
  id: ROOT_CATEGORY_ID,
  name: 'Root',
  children: [
    {
      id: FOOD_CATEGORY_ID,
      name: 'Food',
      children: FOOD_CATEGORY_LIST,
    },
    {
      id: NON_FOOD_CATEGORY_ID,
      name: 'Non Food',
      children: NON_FOOD_CATEGORY_LIST,
    },
  ],
};

interface IDescriptiveSizeUnit {
  enum: string;
  value: string;
  isEan2?: boolean;
}

export const DESCRIPTIVE_SIZE_UNITS_MAP: IDescriptiveSizeUnit[] = [
  { enum: 'GRAM', value: 'gr' },
  { enum: 'KILOGRAM', value: 'kg' },
  { enum: 'MILLILITER', value: 'ml' },
  { enum: 'CENTILITER', value: 'cl' },
  { enum: 'DECILITER', value: 'dl' },
  { enum: 'LITER', value: 'l' },
  { enum: 'ITEM', value: 'st' },
  { enum: 'BAGS', value: 'påsar' },
  { enum: 'PIECE', value: 'bit' },
  { enum: 'GRAM_APPROXIMATE', value: 'gram ungefärlig vikt', isEan2: true },
  // { enum: 'GRAM_PER_ITEM_APPROXIMATE', value: 'gram/st ungefärlig vikt', isEan2: true },
  // { enum: 'GRAM_PER_PIECE_APPROXIMATE', value: 'gram/bit ungefärlig vikt', isEan2: true },

  { enum: 'pack', value: 'pack' },
  { enum: 'set', value: 'set' },
  { enum: 'par', value: 'par' },
  { enum: 'TABLET', value: 'tabletter' },
  { enum: 'CAPSULE', value: 'kapslar' },
  { enum: 'DOSE', value: 'doser' },
  { enum: 'MILLIGRAM', value: 'mg' },
  { enum: 'MICROGRAM', value: 'µg' },
  { enum: 'MICROLITER', value: 'µl' },
  { enum: 'MILLIMETER', value: 'mm' },
  { enum: 'CENTIMETER', value: 'cm' },
  { enum: 'METER', value: 'm' },
];

interface INutritionalUnit {
  enum: string;
  value: string;
}

export const NUTRITIONAL_UNITS_MAP: INutritionalUnit[] = [
  { enum: 'GRAM', value: 'g' },
  { enum: 'GRAM', value: 'gr' },
  { enum: 'GRAM', value: 'gram' },
  { enum: 'MILLILITER', value: 'ml' },
  { enum: 'MILLILITER', value: 'milliliter' },
  { enum: 'DAGSDOS', value: 'dagsdos' },
];

// Units for non-standardised supplement substances (plant extracts, probiotics, enzymes,
// amino acids, fatty acids, etc.). Ordered by expected frequency of use in Swedish supplements.
//
// Vitamins/minerals are *not* covered here — their units are fixed by Annex XIII Part A of
// EU Reg. 1169/2011 (mg / µg) and live on the dedicated Vitamins panel.
//
// Notes on individual units:
//   - CFU / miljoner CFU / miljarder CFU: probiotics; CFU is the Livsmedelsverket-stated
//     declaration for "bakterier eller jästsvampar".
//   - AFU: flow-cytometry method (ISO 19344) — used by some manufacturers but not an EU
//     regulatory standard; CFU remains the convention.
//   - Enzyme activity units: mass (mg) doesn't capture enzymatic activity, so digestive-enzyme
//     labels declare activity in FCC/USP/FIP units. Per enzyme:
//       papain → USP or PU (both seen on real labels);
//       pancreatin / trypsin → USP;
//       bromelain → GDU (alt MCU);
//       fungal protease → HUT;
//       DPP-IV (dipeptidyl peptidase) → DPP-IV (alt HUT);
//       alkaline protease → AP;
//       bacterial protease → PC;
//       acid protease → SAPU;
//       alpha-amylase → DU (alt SKB, BAU);
//       glucoamylase → AGU;
//       invertase / sucrase → SU (Sumner; alt INVU);
//       alpha-galactosidase → GalU;
//       beta-glucanase → BGU;
//       maltase → DP (Degrees of Diastatic Power);
//       pectinase → endo-PGU (alt AJDU);
//       lactase → ALU;
//       cellulase → CU;
//       hemicellulase → HCU;
//       xylanase → XU;
//       lipase → FIP (alt LU);
//       nattokinase → FU;
//       serrapeptase → SPU;
//       lumbrokinase → LKU;
//       phytase → FTU;
//       catalase → Baker Units.
//     Casing intentionally mixes (USP, GalU, endo-PGU, Baker Units, DPP-IV) to match how each
//     unit is conventionally written on real labels — string comparisons must be exact-match.
//     Deliberately excluded: IU for Superoxide Dismutase (same unit as the IE we removed
//     earlier — English vs Swedish abbreviation; producers can declare SOD by mass instead).
export const SUPPLEMENT_SUBSTANCE_UNITS: { value: string; group: string }[] = [
  { value: 'mg', group: 'Massvärden' },
  { value: 'µg', group: 'Massvärden' },
  { value: 'g', group: 'Massvärden' },
  { value: 'ml', group: 'Volym' },
  { value: 'miljarder CFU', group: 'Probiotika' },
  { value: 'miljoner CFU', group: 'Probiotika' },
  { value: 'CFU', group: 'Probiotika' },
  { value: 'AFU', group: 'Probiotika' },
  // Proteases
  { value: 'USP', group: 'Enzymaktivitet' },
  { value: 'PU', group: 'Enzymaktivitet' },
  { value: 'HUT', group: 'Enzymaktivitet' },
  { value: 'DPP-IV', group: 'Enzymaktivitet' },
  { value: 'AP', group: 'Enzymaktivitet' },
  { value: 'PC', group: 'Enzymaktivitet' },
  { value: 'SAPU', group: 'Enzymaktivitet' },
  { value: 'GDU', group: 'Enzymaktivitet' },
  { value: 'MCU', group: 'Enzymaktivitet' },
  // Carbohydrases
  { value: 'DU', group: 'Enzymaktivitet' },
  { value: 'SKB', group: 'Enzymaktivitet' },
  { value: 'BAU', group: 'Enzymaktivitet' },
  { value: 'AGU', group: 'Enzymaktivitet' },
  { value: 'SU', group: 'Enzymaktivitet' },
  { value: 'INVU', group: 'Enzymaktivitet' },
  { value: 'GalU', group: 'Enzymaktivitet' },
  { value: 'BGU', group: 'Enzymaktivitet' },
  { value: 'DP', group: 'Enzymaktivitet' },
  { value: 'endo-PGU', group: 'Enzymaktivitet' },
  { value: 'AJDU', group: 'Enzymaktivitet' },
  // Lactase / fiber
  { value: 'ALU', group: 'Enzymaktivitet' },
  { value: 'CU', group: 'Enzymaktivitet' },
  { value: 'HCU', group: 'Enzymaktivitet' },
  { value: 'XU', group: 'Enzymaktivitet' },
  // Lipases
  { value: 'FIP', group: 'Enzymaktivitet' },
  { value: 'LU', group: 'Enzymaktivitet' },
  // Specialty enzymes
  { value: 'FU', group: 'Enzymaktivitet' },
  { value: 'SPU', group: 'Enzymaktivitet' },
  { value: 'LKU', group: 'Enzymaktivitet' },
  { value: 'FTU', group: 'Enzymaktivitet' },
  { value: 'Baker Units', group: 'Enzymaktivitet' },
];
export const supplementSubstanceUnits = SUPPLEMENT_SUBSTANCE_UNITS.map(({ group, value }) => ({ displayName: group, value }));

// Daily-dose units for the kosttillskott "dagsdos" basis (the per-X nutrition denominator,
// e.g. "2 kapslar"). Dedicated curated list so we don't widen the shared nettokvantitet vocab
// (DESCRIPTIVE_SIZE_UNITS_MAP); the stored dagsdosUnit is the Swedish value itself — same
// convention as SUPPLEMENT_SUBSTANCE_UNITS above.
export const DAGSDOS_UNITS_MAP: { value: string }[] = [
  // Solid oral forms
  { value: 'kapslar' },
  { value: 'tabletter' },
  { value: 'tuggtabletter' },
  { value: 'brustabletter' },
  { value: 'pastiller' },
  // Powder
  { value: 'skopor' },
  { value: 'gram' },
  // Liquid forms
  { value: 'ml' },
  { value: 'droppar' },
  { value: 'matskedar' },
  { value: 'teskedar' },
  { value: 'sprayningar' },
  { value: 'ampuller' },
  // Sachet / generic
  { value: 'portionspåsar' },
  { value: 'doser' },
  { value: 'st' },
];
export const dagsdosUnit = DAGSDOS_UNITS_MAP.map(({ value }) => ({ displayName: value, value }));

export const getDescriptiveSizeEnumFromValue = (value: string) => {
  const v = DESCRIPTIVE_SIZE_UNITS_MAP.find(x => x.value === value);
  return isDefined(v) ? v : '';
};

export const getDescriptiveSizeValueFromEnum = (enumType: string) => {
  const v = DESCRIPTIVE_SIZE_UNITS_MAP.find(x => x.enum === enumType);
  return v?.value ?? '';
};

export const getNutritionalEnumFromValue = (value: string) => {
  const v = NUTRITIONAL_UNITS_MAP.find(x => x.value === value);
  return isDefined(v) ? v : '';
};

export const getNutritionalValueFromEnum = (enumType: string) => {
  const v = NUTRITIONAL_UNITS_MAP.find(x => x.enum === enumType);
  return isDefined(v) ? v : '';
};

const PRODUCT_NOTE_INPUT_TYPES = {
  INT_ARRAY: 'intArrayValue',
};

const intArrayReducer = (ack: string, key: string) => ack.concat(`${key},`);

interface IProductNoteTypesExtractor {
  [key: string]: (value: string[]) => string;
}

export const PRODUCT_NOTE_TYPES_EXTRACTOR: IProductNoteTypesExtractor = {
  ProductNoteIntArray: (value: string[]) => {
    // TODO
    if (isEmpty(value)) return '';
    return value.reduce(intArrayReducer, '').slice(0, -1);
  },
};

interface IProductNotesTypeTransformers {
  [key: string]: (valueToTransform: string) => number[];
}

// TODO: Implemnt failure handling.
// TODO: Go over this
export const PRODUCT_NOTES_TYPE_TRANSFORMERS: IProductNotesTypeTransformers = {
  [PRODUCT_NOTE_INPUT_TYPES.INT_ARRAY]: (valueToTransform: string) => {
    if (valueToTransform) {
      return map((v: string) => parseInt(v))(valueToTransform.split(','));
    } else {
      return [];
    }
  },
};

interface IOrganizationSpecificField {
  fieldName: string;
  inputType: string; // TODO type
  formLabel: string;
  info: string;
  transformer: (value: string) => number[];
}

interface IOrganizationSpecificFields {
  [key: string]: IOrganizationSpecificField[];
}

export const ORGANIZATION_SPECIFIC_FIELDS: IOrganizationSpecificFields = {
  COOP: [
    {
      fieldName: 'ItemCategoriesOnline',
      inputType: PRODUCT_NOTE_INPUT_TYPES.INT_ARRAY,
      formLabel: 'Item Categories Online',
      info: 'Accepts a list of numbers, each entry should be separated by a comma.',
      transformer: PRODUCT_NOTES_TYPE_TRANSFORMERS[PRODUCT_NOTE_INPUT_TYPES.INT_ARRAY],
    },
  ],
};

export const PRODUCT_CLASSIFICATIONS = ['KLASS ”EXTRA”', 'KLASS 1', 'KLASS 2'];

export const requiresClassification = (foodlaCategory?: IFoodlaCategory) => {
  return foodlaCategory?.name === 'Frukter, bär och grönsaker';
};

interface ILabel {
  title: string;
  field: string;
  isFood: boolean;
  isNonFood: boolean;
}

export const URSPRUNG_LABELS: ILabel[] = [
  { title: 'Från Sverige', field: 'fromSweden', isFood: true, isNonFood: true },
  { title: 'Kött från Sverige', field: 'kottFranSverige', isFood: true, isNonFood: false },
  { title: 'Mjölk från Sverige', field: 'milkSweden', isFood: true, isNonFood: false },
  { title: 'Svensk fågel', field: 'svenskFagel', isFood: true, isNonFood: false },
  { title: 'Gott Från Finland', field: 'goodsFromFinlandBlueSwan', isFood: true, isNonFood: false },
  { title: 'SUB / DOP / PDO', field: 'protectedDesignationOfOrigin', isFood: true, isNonFood: false },
  { title: 'SGB / IGP / PGI', field: 'protectedGeographicalIndication', isFood: true, isNonFood: false },
  { title: 'GTS / TSG', field: 'traditionalSpecialtyGuaranteed', isFood: true, isNonFood: false },
];

export const EKOLOGISKT_LABELS: ILabel[] = [
  { title: 'Ekologiskt', field: 'ecological', isFood: true, isNonFood: false },
  { title: 'EU-lövet', field: 'EULeaf', isFood: true, isNonFood: false },
  { title: 'KRAV', field: 'kravmarkt', isFood: true, isNonFood: false },
  { title: 'USDA', field: 'USDA', isFood: true, isNonFood: true },
  { title: 'Australian Certified Organic', field: 'australianOrganic', isFood: true, isNonFood: true },
  { title: 'Soil Association', field: 'soliAssociation', isFood: true, isNonFood: true },
  { title: 'Annan EKO märkning/ certifiering', field: 'otherLabelingECO', isFood: true, isNonFood: true },
  { title: 'Demeter (orangea loggan)', field: 'demeterLabel', isFood: true, isNonFood: true },
];

export const HALLBARHET_LABELS: ILabel[] = [
  { title: 'Fairtrade', field: 'fairtrade', isFood: true, isNonFood: true },
  { title: 'Fairtrade Cacao', field: 'fairtradeCacao', isFood: true, isNonFood: false },
  { title: 'Fairtrade Sugar', field: 'fairtradeSugar', isFood: true, isNonFood: false },
  { title: 'Rainforest alliance', field: 'rainforestAlliance', isFood: true, isNonFood: true },
  { title: 'Svenskt sigill', field: 'svensktSigill', isFood: true, isNonFood: true },
  { title: 'Svenskt sigill klimatcertifierad', field: 'svensktSigillKlimatcertifierad', isFood: true, isNonFood: true },
  { title: 'Svenskt sigill naturbeteskött', field: 'svensktSigillNaturbeteskott', isFood: true, isNonFood: false },
  { title: 'USDA Biobased', field: 'usdaCertifiedBiobased', isFood: false, isNonFood: true },
  { title: 'Återvinningsbar', field: 'recyclableGeneralClaim', isFood: true, isNonFood: true },
  { title: 'Engångsplast märknings', field: 'madeOfPlasticBeverageCups', isFood: true, isNonFood: true },
  { title: 'Green dot återvinningssymbol', field: 'greenDot', isFood: true, isNonFood: true },
];

export const HALSA_KOST_LABELS: ILabel[] = [
  { title: 'Nyckelhål', field: 'nyckelhal', isFood: true, isNonFood: false },
  { title: 'Äkta Vara', field: 'aktaVara', isFood: true, isNonFood: false },
  {
    title: 'Finska hjärtmärket (motsvarande nyckelhålet)',
    field: 'finnishHeartSymbol',
    isFood: true,
    isNonFood: false,
  },
  { title: 'GMO-fri Pro Terra', field: 'proTerraNonGmoCertification', isFood: true, isNonFood: false },
];

export const NARINGSPASTAENDE_LABELS: ILabel[] = [
  { title: 'Hög fiberhalt', field: 'hogFiberhalt', isFood: true, isNonFood: false },
  { title: 'Låg fetthalt', field: 'lowFat', isFood: true, isNonFood: false },
  { title: 'Utan tillsatt socker', field: 'noAddedSugar', isFood: true, isNonFood: false },
];

export const FRI_FRAN_ALLERGI_LABELS: ILabel[] = [
  { title: 'Glutenfri (text)', field: 'glutenFree', isFood: true, isNonFood: false },
  { title: 'Laktosfri', field: 'lactoseFree', isFood: true, isNonFood: false },
  { title: 'Fri från mjölkprotein', field: 'friFranMjolkprotein', isFood: true, isNonFood: false },
  { title: 'Äggfri', field: 'aggfri', isFood: true, isNonFood: false },
  { title: 'Låg sockerhalt', field: 'lowSugar', isFood: true, isNonFood: false },
  { title: 'Glutenfri, Det överkorsade axet', field: 'crossedGrainSymbol', isFood: true, isNonFood: false },
  { title: 'Sockerfri', field: 'freeFromSugars', isFood: true, isNonFood: false },
  { title: 'Låg-laktos', field: 'lowLactose', isFood: true, isNonFood: false },
  { title: 'Mjölkfri', field: 'freeFromMilk', isFood: true, isNonFood: false },
  { title: 'Sojafri', field: 'freeFromSoy', isFood: true, isNonFood: false },
];

export const DIET_VEG_LABELS: ILabel[] = [
  { title: 'Veganskt', field: 'vegan', isFood: true, isNonFood: true },
  { title: "I'm Vegan", field: 'iAmVegan', isFood: true, isNonFood: true },
  { title: 'Vegetariskt', field: 'vegetarian', isFood: true, isNonFood: false },
  { title: 'V-label vegan', field: 'vLabelVegan', isFood: true, isNonFood: true },
  { title: 'Vegan Trademark', field: 'veganTrademark', isFood: true, isNonFood: true },
  { title: 'Certified Vegan', field: 'certifiedVegan', isFood: true, isNonFood: true },
  { title: 'Vegan Society', field: 'veganSociety', isFood: true, isNonFood: true },
  { title: 'Annan vegansk märkning/ certifiering', field: 'otherLabelingVegan', isFood: true, isNonFood: true },
  { title: 'V-Label Vegetarian', field: 'europeanVLabelVegetarian', isFood: true, isNonFood: true },
  { title: 'Vegetarian society approved Vegetarian', field: 'vegetarianSocietyVLogo', isFood: true, isNonFood: true },
  { title: 'Halal (ICC Halal of Scandinavia)', field: 'halalIslamicCulturalCenterOfScandinavia', isFood: true, isNonFood: true },
  { title: 'Kosher Dairy', field: 'kosherOkDairy', isFood: true, isNonFood: false },
  { title: 'Orthodox Union kosher', field: 'ouKosher', isFood: true, isNonFood: false },
];

export const DJURVANLIGT_LABELS: ILabel[] = [
  {
    title: 'Beauty without Bunnies – Cruelty Free and Vegan',
    field: 'crueltyFreeVegan',
    isFood: false,
    isNonFood: true,
  },
  { title: 'Leaping Bunny', field: 'leapingBunny', isFood: false, isNonFood: true },
];

export const FISK_SKALDJUR_LABELS: ILabel[] = [
  { title: 'MSC', field: 'MSC', isFood: true, isNonFood: false },
  { title: 'ASC', field: 'ASC', isFood: true, isNonFood: false },
];

export const VARNING_LABELS: ILabel[] = [
  { title: 'Hög Koffeinhalt', field: 'highCoffein', isFood: true, isNonFood: false },
];

export const MILJO_KEM_LABELS: ILabel[] = [
  { title: 'Bra Miljöval', field: 'braMiljoval', isFood: false, isNonFood: true },
  { title: 'Svanen', field: 'svanen', isFood: true, isNonFood: true },
  { title: 'EU Ecolabel (Eu-blomman)', field: 'EUEcolabel', isFood: true, isNonFood: true },
  {
    title: 'Rekommenderas av Astma och Allergiförbundet',
    field: 'approvedByAsthmaAndAllergyAssoc',
    isFood: true,
    isNonFood: true,
  },
  { title: 'Annan miljö märkning/ certifiering', field: 'otherLabelingEnvironment', isFood: true, isNonFood: true },
  // Apotea LIMA v3.1
  { title: 'Blue Angel (Blå Ängel)', field: 'blueAngel', isFood: false, isNonFood: true },
];

export const MATERIAL_TRA_LABELS: ILabel[] = [
  { title: 'FSC', field: 'FSCStewardshipCouncil', isFood: true, isNonFood: true },
  { title: 'FSC 100%', field: 'FSC', isFood: true, isNonFood: true },
  { title: 'FSC Mix', field: 'FSCStewardshipCouncilMix', isFood: true, isNonFood: true },
  { title: 'FSC Recycled', field: 'FSCStewardshipCouncilRecycled', isFood: true, isNonFood: true },
  { title: 'PEFC', field: 'PEFC', isFood: true, isNonFood: true },
];

export const TEXTIL_LABELS: ILabel[] = [
  { title: 'GOTS', field: 'globalOrganicTextileStandard', isFood: false, isNonFood: true },
  { title: 'OEKO-TEX Standard 100', field: 'oekoTex', isFood: false, isNonFood: true },
  { title: 'OEKO-TEX Made in Green', field: 'oekoTexGreen', isFood: false, isNonFood: true },
  { title: 'Fairtrade Cotton', field: 'fairtradeCotton', isFood: false, isNonFood: true },
  { title: 'Better Cotton Initiative (BCI)', field: 'betterCottonInitiative', isFood: false, isNonFood: true },
  { title: 'OCS 100 (Organic Content Standard)', field: 'organic100ContentStandard', isFood: false, isNonFood: true },
];

export const HYGIEN_KOSMETIK_LABELS: ILabel[] = [
  { title: 'ECOCERT', field: 'ecoCert', isFood: true, isNonFood: true },
  { title: 'Cosmos ORGANIC - certified by Ecocert', field: 'ecocertCosmosOrganic', isFood: false, isNonFood: true },
  { title: 'Cosmos NATURAL - certified by Ecocert', field: 'ecocertCosmosNatural', isFood: false, isNonFood: true },
  { title: 'Natrue', field: 'nature', isFood: false, isNonFood: true },
  { title: 'BDIH', field: 'BDIH', isFood: false, isNonFood: true },
  { title: 'NSF', field: 'NSF', isFood: true, isNonFood: true },
  { title: 'EcoControl (COSMOS)', field: 'ecoControl', isFood: false, isNonFood: true },
  { title: 'Oasis', field: 'oasis', isFood: false, isNonFood: true },
  { title: 'Parfymfri', field: 'freeFromFragrance', isFood: false, isNonFood: true },
  // Apotea LIMA v3.1 — Cosmos NATURAL (11 new certifiers; Ecocert variant already above)
  {
    title: 'Cosmos NATURAL - certified by ACO (Australian Certified Organic)',
    field: 'cosmosNaturalACO',
    isFood: false,
    isNonFood: true,
  },
  {
    title: 'Cosmos NATURAL - certified by Bureau Veritas',
    field: 'cosmosNaturalBureauVeritas',
    isFood: false,
    isNonFood: true,
  },
  { title: 'Cosmos NATURAL - certified by CAAE', field: 'cosmosNaturalCAAE', isFood: false, isNonFood: true },
  {
    title: 'Cosmos NATURAL - certified by Control Union Korea',
    field: 'cosmosNaturalControlUnionKorea',
    isFood: false,
    isNonFood: true,
  },
  { title: 'Cosmos NATURAL - certified by Cosmebio', field: 'cosmosNaturalCosmebio', isFood: false, isNonFood: true },
  { title: 'Cosmos NATURAL - certified by Etko', field: 'cosmosNaturalEtko', isFood: false, isNonFood: true },
  { title: 'Cosmos NATURAL - certified by ICEA', field: 'cosmosNaturalICEA', isFood: false, isNonFood: true },
  {
    title: 'Cosmos NATURAL - certified by IFC Global',
    field: 'cosmosNaturalIFCGlobal',
    isFood: false,
    isNonFood: true,
  },
  { title: 'Cosmos NATURAL - certified by IONC (BDIH)', field: 'cosmosNaturalIONC', isFood: false, isNonFood: true },
  {
    title: 'Cosmos NATURAL - certified by KTR (Korea testing & research)',
    field: 'cosmosNaturalKTR',
    isFood: false,
    isNonFood: true,
  },
  {
    title: 'Cosmos NATURAL - certified by Soil Association',
    field: 'cosmosNaturalSoilAssociation',
    isFood: false,
    isNonFood: true,
  },
  // Apotea LIMA v3.1 — Cosmos ORGANIC (11 new certifiers; Ecocert variant already above)
  {
    title: 'Cosmos ORGANIC - certified by ACO (Australian Certified Organic)',
    field: 'cosmosOrganicACO',
    isFood: false,
    isNonFood: true,
  },
  {
    title: 'Cosmos ORGANIC - certified by Bureau Veritas',
    field: 'cosmosOrganicBureauVeritas',
    isFood: false,
    isNonFood: true,
  },
  { title: 'Cosmos ORGANIC - certified by CAAE', field: 'cosmosOrganicCAAE', isFood: false, isNonFood: true },
  {
    title: 'Cosmos ORGANIC - certified by Control Union Korea',
    field: 'cosmosOrganicControlUnionKorea',
    isFood: false,
    isNonFood: true,
  },
  { title: 'Cosmos ORGANIC - certified by Cosmebio', field: 'cosmosOrganicCosmebio', isFood: false, isNonFood: true },
  { title: 'Cosmos ORGANIC - certified by Etko', field: 'cosmosOrganicEtko', isFood: false, isNonFood: true },
  { title: 'Cosmos ORGANIC - certified by ICEA', field: 'cosmosOrganicICEA', isFood: false, isNonFood: true },
  {
    title: 'Cosmos ORGANIC - certified by IFC Global',
    field: 'cosmosOrganicIFCGlobal',
    isFood: false,
    isNonFood: true,
  },
  { title: 'Cosmos ORGANIC - certified by IONC (BDIH)', field: 'cosmosOrganicIONC', isFood: false, isNonFood: true },
  {
    title: 'Cosmos ORGANIC - certified by KTR (Korea testing & research)',
    field: 'cosmosOrganicKTR',
    isFood: false,
    isNonFood: true,
  },
  {
    title: 'Cosmos ORGANIC - certified by Soil Association',
    field: 'cosmosOrganicSoilAssociation',
    isFood: false,
    isNonFood: true,
  },
  // Apotea LIMA v3.1 — EcoCert (4 disambiguated, the existing ecoCert above is ambiguous)
  { title: 'EcoCert - Natural Cosmetic', field: 'ecocertNaturalCosmetic', isFood: false, isNonFood: true },
  { title: 'EcoCert - Organic Cosmetic', field: 'ecocertOrganicCosmetic', isFood: false, isNonFood: true },
  { title: 'EcoCert - Natural detergent', field: 'ecocertNaturalDetergent', isFood: false, isNonFood: true },
  {
    title: 'EcoCert - Natural detergent made with Organic',
    field: 'ecocertNaturalDetergentOrganic',
    isFood: false,
    isNonFood: true,
  },
  // Apotea LIMA v3.1 — Soil Association (2 disambiguated, the existing soliAssociation in EKOLOGISKT_LABELS is ambiguous)
  { title: 'Soil Association Natural', field: 'soilAssociationNatural', isFood: false, isNonFood: true },
  { title: 'Soil Association Organic', field: 'soilAssociationOrganic', isFood: true, isNonFood: true },
  // Apotea LIMA v3.1 — standalone (Cosmebio Organic, UEBT)
  { title: 'Cosmebio Organic', field: 'cosmebioOrganic', isFood: false, isNonFood: true },
  { title: 'UEBT', field: 'UEBT', isFood: true, isNonFood: true },
];

export const ALL_LABELS = [
  ...URSPRUNG_LABELS,
  ...EKOLOGISKT_LABELS,
  ...HALLBARHET_LABELS,
  ...HALSA_KOST_LABELS,
  ...NARINGSPASTAENDE_LABELS,
  ...FRI_FRAN_ALLERGI_LABELS,
  ...DIET_VEG_LABELS,
  ...DJURVANLIGT_LABELS,
  ...FISK_SKALDJUR_LABELS,
  ...VARNING_LABELS,
  ...MILJO_KEM_LABELS,
  ...MATERIAL_TRA_LABELS,
  ...TEXTIL_LABELS,
  ...HYGIEN_KOSMETIK_LABELS,
];

export const APOTEA_LABEL_NAME: Record<string, string> = {
  Svanen: 'svanen',
  MSC: 'MSC',
  'Vegan Trademark': 'veganTrademark',
  FSC: 'FSC',
  KRAV: 'kravmarkt',
  'Cosmos ORGANIC - certified by Ecocert': 'ecocertCosmosOrganic',
  'EU-Ekologiskt': 'ecological',
};

export const mapLabelsByCategory = ({
  labels,
  foodlaCategory,
  rootCategory,
}: {
  labels: ILabel[];
  foodlaCategory?: IFoodlaCategory;
  rootCategory?: IFoodlaCategory;
}) => {
  const isFood = isNewFood(foodlaCategory, rootCategory);
  const isNonFood = isNewNonFood(foodlaCategory, rootCategory);
  if (isFood) {
    return labels.filter(label => label.isFood);
  }
  if (isNonFood) {
    return labels.filter(label => label.isNonFood);
  }
  return labels;
};

export const getAllLabelsByCategrory = (foodlaCategory?: IFoodlaCategory, rootCategory?: IFoodlaCategory) => {
  return mapLabelsByCategory({ labels: ALL_LABELS, foodlaCategory, rootCategory });
};

export const getPercentageValues = () => {
  const values: number[] = new Array(35).fill(0.1);
  const strValues: string[] = new Array(35).fill('');
  for (let i = 1; i < 36; i++) {
    strValues[i - 1] = (values[i - 1] * i).toFixed(1);
  }
  // TODO
  // values.splice(0, 0, 0.0);
  return strValues;
};

export const optionalAlcoholPercentage = (foodlaCategory?: IFoodlaCategory) => {
  return equals(foodlaCategory?.name, 'Drycker');
};

export enum PackageSizeUnit {
  styck = 'styck',
  pack = 'pack',
  set = 'set',
  par = 'par',
}

export const PRODUCT_DATE_FIELD: (keyof IProduct)[] = [
  'basicDataAvailabilityDateFrom',
  'articleFirstOrderDate',
  'articleFirstDelivery',
];

export const PRODUCT_ENUM_FIELD: (keyof IProduct)[] = [
  'basicDataTaxClassification',
  'purchasingDataIncoterm',
  'purchasingDataPickupLocation',
  'package_size_unit',
  'descriptive_size_unit',
  'nutritional_unit',
  'classification',

  // new
  'securityNotices',
  'hazardStatements',
  'animalFeedType',
  'animalCategoriesForFeed',
  'labels',
  'hazardSymbols',
  'sunProtectionFactor',
  'categoryWeightloss',
  'preparedForm',
  'signalWords',
  'recyclingType',
  'eggWeightClass',
  'catchArea',
  'fishCatchMethod',
  'fishProductionMethod',
  'additionalCharacteristics',
  'complianceMetalsSkinContact',
  'confirmGuidanceSwedishElectronics',
  'qrNutritionClaimsCode',
  'qrHealthClaimsCode',
  'qrElectronicCode',
  'complianceToyDirective',
  'complianceBiocid',
  'complianceBiocidalTreated',
  'complianceSunscreenRegulation',

  // boolean + enum
  'approvalDietFeed',
  'ironing',

  'containsBattery',
  'batteryType',
  'batteryTechnology',
  'batteryHazardousMetals',
  'lampEnergyClass',
  'dagsdosUnit',
  'flammableSubtype',
];

export const PRODUCT_ENUM_FIELD_VALUES: Record<string, { value: string; convertTo?: string | number }[]> = {
  basicDataTaxClassification: [
    // set [percent value] first to convert from saved product
    { value: '0%', convertTo: 0 },
    { value: '6%', convertTo: 6 },
    { value: '12%', convertTo: 12 },
    { value: '25%', convertTo: 25 },
    { value: '0', convertTo: 0 },
    { value: '6', convertTo: 6 },
    { value: '12', convertTo: 12 },
    { value: '25', convertTo: 25 },
  ],
  purchasingDataIncoterm: [
    { value: 'DDP (Delivered Duty Paid)', convertTo: 'DDP' },
    { value: 'DDP' },
    { value: 'FCA (Free Carrier)', convertTo: 'FCA' },
    { value: 'FCA' },
  ],
  purchasingDataPickupLocation: [
    { value: 'D100' },
    { value: 'D200' },
    { value: 'D300' },
    { value: 'D210' },
    { value: 'D400' },
    { value: 'D500' },
  ],
  package_size_unit: [
    { value: 'st', convertTo: PackageSizeUnit.styck },
    { value: 'styck', convertTo: PackageSizeUnit.styck },
    { value: 'pack', convertTo: PackageSizeUnit.pack },
    { value: 'par', convertTo: PackageSizeUnit.par },
  ],
  descriptive_size_unit: [
    { value: 'gram', convertTo: 'GRAM' },
    { value: 'gr', convertTo: 'GRAM' },
    { value: 'g', convertTo: 'GRAM' },
    { value: 'kg', convertTo: 'KILOGRAM' },
    { value: 'st', convertTo: 'ITEM' },
    { value: 'ml', convertTo: 'MILLILITER' },
    { value: 'l', convertTo: 'LITER' },
    { value: 'gram ungefärlig vikt', convertTo: 'GRAM_APPROXIMATE' },
    { value: 'tabletter', convertTo: 'TABLET' },
    { value: 'kapslar', convertTo: 'CAPSULE' },
    { value: 'doser', convertTo: 'DOSE' },
    { value: 'mg', convertTo: 'MILLIGRAM' },
    { value: 'µg', convertTo: 'MICROGRAM' },
    { value: 'µl', convertTo: 'MICROLITER' },
    { value: 'mm', convertTo: 'MILLIMETER' },
    { value: 'cm', convertTo: 'CENTIMETER' },
    { value: 'm', convertTo: 'METER' },
  ],
  nutritional_unit: [
    { value: 'gram', convertTo: NutritionalUnit.GRAM },
    { value: 'gr', convertTo: NutritionalUnit.GRAM },
    { value: 'g', convertTo: NutritionalUnit.GRAM },
    { value: 'ml', convertTo: NutritionalUnit.MILLILITER },
    { value: 'milliliter', convertTo: NutritionalUnit.MILLILITER },
    { value: 'dagsdos', convertTo: NutritionalUnit.DAGSDOS },
  ],
  containsBattery: [
    { value: 'Ja - litiumbatterier', convertTo: BatteryEnum.LITHIUM },
    { value: 'Ja - övriga batterier', convertTo: BatteryEnum.OTHER },
    { value: 'Nej', convertTo: BatteryEnum.NONE },
  ],
  complianceSunscreenRegulation: [
    { value: 'Ja', convertTo: 'YES' },
    { value: 'Nej', convertTo: 'NO' },
    { value: 'Ej solskyddsmedel', convertTo: 'NO_SUNSCREEN' },
  ],
  qrNutritionClaimsCode: [
    { value: 'Ja', convertTo: 'YES' },
    { value: 'Nej', convertTo: 'NO' },
    { value: 'Inga näringspåståenden', convertTo: 'NO_NUTRITION_CLAIMS' },
    { value: 'YES' },
    { value: 'NO' },
    { value: 'NO_NUTRITION_CLAIMS' },
  ],
  qrHealthClaimsCode: [
    { value: 'Ja', convertTo: 'YES' },
    { value: 'Nej', convertTo: 'NO' },
    { value: 'Inga hälsopåståenden', convertTo: 'NO_HEALTH_CLAIMS' },
    { value: 'YES' },
    { value: 'NO' },
    { value: 'NO_HEALTH_CLAIMS' },
  ],
  qrElectronicCode: [
    { value: 'Ja', convertTo: 'YES' },
    { value: 'Nej', convertTo: 'NO' },
    { value: 'Ej elektrisk', convertTo: 'NOT_ELECTRICAL' },
    { value: 'YES' },
    { value: 'NO' },
    { value: 'NOT_ELECTRICAL' },
  ],
};

export const PRODUCT_NUMBER_FIELD: (keyof IProduct)[] = [
  'gross_weight_num',
  'gross_height',
  'gross_width',
  'gross_depth',
  'consumer_size_to_order_size_ratio',
  'package_size',
  'transportMinTemperature',
  'transportMaxTemperature',

  'min_temperature',
  'max_temperature',
  'descriptive_size_amount',
  'descriptive_size_amount_extra',

  'energi_kj',
  'energi_kcal',
  'fett',
  'mattatFett',
  'kolhydrat',
  'sockerarter',
  'fiber',
  'protein',
  'salt',
  'flerromattatFett',
  'enkelomattatFett',

  // Consumer Pack
  'consumerPackNetWeight',
  // Store Pack
  'storePackTradeItemMeasurementsLengthDepth',
  'storePackTradeItemMeasurementsWidth',
  'storePackTradeItemMeasurementsHeight',
  'storePackGrossWeight',
  // Transport Pack
  'transportQtyOfNextLowerItem',
  'transportTradeItemMeasurementsLengthDepth',
  'transportTradeItemMeasurementsWidth',
  'transportTradeItemMeasurementsHeight',
  'transportGrossWeight',
  // Pallet Layer
  'palletLayerQtyOfNextLowerItem',
  'palletLayerTradeItemMeasurementsLengthDepth',
  'palletLayerTradeItemMeasurementsWidth',
  'palletLayerTradeItemMeasurementsHeight',
  'palletLayerGrossWeight',
  // Pallet
  'palletQtyOfNextLowerItem',
  'palletQtyOfStorePack',
  'palletQtyOfBaseItem',
  'transportQtyOfBaseItem',
  'palletTradeItemMeasurementsLengthDepth',
  'palletTradeItemMeasurementsWidth',
  'palletTradeItemMeasurementsHeight',
  'palletGrossWeight',
  // Price
  'priceListPrice',
  'priceRecommendedConsumerPrice',

  'whiteSnusNicotinePerPortion',
  'whiteSnusNicotinePerGram',
  'whiteSnusPortionsPerUnit',
  'whiteSnusWeightPerPortion',

  'apoteaPurchasePrice',
  'apoteaListPrice',
  'apoteaStorePrice',
  'apoteaStorePriceNok',
  'lavQuantity',

  'batteryVoltage',
  'batteryCapacity',
  'batteryLife',
  'batteryWeight',
  'lampEffect',
  'lampBrightnessLm',
  'lampColorTemperatureK',
  'lampAverageLifespanH',
  'lampColorRenderingRa',
  'lampEnergyConsumption',
  'lampBeamAngleDeg',

  'burningTime',

  'recycling_amount',

  'dagsdosAmount',
];

export const NON_NEGATIVE_NUMBER_FIELDS = [
  'apoteaPurchasePrice',
  'apoteaListPrice',
  'apoteaStorePrice',
  'apoteaStorePriceNok',
  'lavQuantity',
];

export const PRODUCT_BOOLEAN_FIELD: (keyof IProduct)[] = [
  'basicDataMixCategory',
  'basicDataTradeItemVariableUnit',
  'basicDataCbuIndicator',

  // Consumer Pack
  'consumerPackOrderableUnit',
  'consumerPackDespatchUnit',
  // Store Pack
  'storePackOrderableUnit',
  'storePackDespatchUnit',
  // Transport Pack
  'transportOrderableUnit',
  'transportDespatchUnit',
  // Pallet Layer
  'palletLayerOrderableUnit',
  'palletLayerDespatchUnit',
  // Pallet
  'palletOrderableUnit',
  'palletDespatchUnit',

  // questions
  'reachSubstance',
  'bestBeforeDate',
  'childrenUnder16',
  'reachSubstancesAnnex19072006',
  'hazardousSubstancesOver1',
  'complianceMedicalTechProducts',
  'checkUniqueIdentification',
  'cmrSubstance',
  'approvalDietFeed',
  'ceMarking',

  // Apotea Kvalitet-sheet questions (boolean Yes/No attestations).
  // Surfaced in bulk import per sheet 3.1–3.8 alignment audit.
  'qrLabelingSwedish',
  'qrMedicalClaims',
  'qrMarketingClaimsCosmetic',
  'qrEfsaUlValues',
  'qrFoodHygiene',
  'qrFoodLabeling',
  'qrMisleadingSalesTerms',
  'qrClassificationLabelingPackaging',
  'qrAdditivesInFeed',
  'qrFeedLabeling',
  'qrRelevantFeedCertifications',
  'complianceEg12232009',
  'complianceReach19072006',
  'complianceProductSafety2004451',
  'complianceHaccp',
  'compliancePops',
  'containsBannedSubstancesPops',

  'plasticTrayPackaging',
  'compliancePpwrPackaging',

  'tumbleDrying',
  'dryCleaning',
  'chlorineBleaching',

  'shrinkWrapPackaging',
  'batteryRechargeable',
  'lampDimmable',
];

export const PRODUCT_EAN_FIELDS: (keyof IProduct)[] = [
  'EAN',
  'storePackEANForStorePack',
  'transportEANForStorePack',
  'palletLayerEANForStorePack',
  'palletEANForStorePack',
];

export const PRODUCT_BOOLEAN_FIELD_VALUES: Record<string, { valueList: string[]; convertTo: boolean }[]> = {
  all: [
    { valueList: ['yes', 'true', '1', 'Ja', 'ja'], convertTo: true },
    { valueList: ['no', 'false', '0', 'Nej', 'nej'], convertTo: false },
  ],
  basicDataMixCategory: [
    { valueList: ['single article', 'single'], convertTo: false },
    { valueList: ['mix article', 'mix'], convertTo: true },
  ],
};

export const MANDATORY_FOOD_NUTRITIONAL_FIELD_SET = new Set<keyof IProduct>([
  'energi_kj',
  'energi_kcal',
  'fett',
  'mattatFett',
  'kolhydrat',
  'sockerarter',
  'protein',
  'salt',
  'nutritional_unit',
]);

export const ALWAYS_DISPLAY_FOOD_FIELD_SET = new Set<keyof IProduct>([
  'title', // Produktnamn / Beteckning
  'EAN', // EAN-kod
  'ingredient_statement', // Ingrediensförteckning
  ...Array.from(MANDATORY_FOOD_NUTRITIONAL_FIELD_SET), // Näringsdeklaration
  'descriptive_size_amount', // Nettokvantitet mängd
  'descriptive_size_unit', // Nettokvantitet enhet
  'trade_item_temperature_information', // Förvaringsanvisning
  'place_of_item_activity', // Ursprungsland och härkomstplats
  'brand_food', // Varumärke
]);

export const MANDATORY_FOOD_FIELD_SET = new Set<keyof IProduct>([
  'title',
  'EAN',
  'ingredient_statement',
  'descriptive_size_amount',
  'descriptive_size_unit',
  'trade_item_temperature_information',
  'place_of_item_activity',
  'brand',

  // TODO make main set
  'basicDataMixCategory',
  'basicDataTradeItemVariableUnit',
  'basicDataTaxClassification',
  'basicDataPriceComparisonContentType',
  'basicDataAvailabilityDateFrom',
  'basicDataCbuIndicator',
  // Consumer Pack
  'consumerPackOrderableUnit',
  'consumerPackDespatchUnit',
  'consumerPackNetWeight',
  // Store Pack
  'storePackEANForStorePack',
  'storePackOrderableUnit',
  'storePackDespatchUnit',
  'storePackTradeItemMeasurementsLengthDepth',
  'storePackTradeItemMeasurementsWidth',
  'storePackTradeItemMeasurementsHeight',
  'storePackGrossWeight',
  // Transport Pack
  // Pallet Layer
  'palletLayerQtyOfNextLowerItem',
  'palletLayerOrderableUnit',
  'palletLayerDespatchUnit',
  'palletLayerTradeItemMeasurementsLengthDepth',
  'palletLayerTradeItemMeasurementsWidth',
  'palletLayerTradeItemMeasurementsHeight',
  'palletLayerGrossWeight',
  // Pallet
  'palletEANForStorePack',
  'palletQtyOfNextLowerItem',
  'palletOrderableUnit',
  'palletDespatchUnit',
  'palletTradeItemMeasurementsLengthDepth',
  'palletTradeItemMeasurementsWidth',
  'palletTradeItemMeasurementsHeight',
  'palletGrossWeight',
  // Price
  'priceListPriceCurrency',
  'priceListPrice',
  'priceRecommendedConsumerPrice',
  // Purchasing Data
  'purchasingDataIncoterm',
  'purchasingDataPickupLocation',
]);

export const ALWAYS_DISPLAY_NON_FOOD_FIELD_SET = new Set<keyof IProduct>([
  'title', // Produktnamn / Beteckning
  'EAN', // EAN-kod
  'package_size', // Förpackningsstorlek Mängd
  'package_size_unit', // Förpackningsstorlek Enhet
  'country_of_manufacturing_string', // Tillverkningsland
  'brand', // Varumärke
]);

export const MANDATORY_NON_FOOD_FIELD_SET = new Set<keyof IProduct>([
  'title',
  'EAN',
  'package_size',
  'package_size_unit',
  'country_of_manufacturing_string',
  'brand',

  // TODO make main set
  'basicDataMixCategory',
  'basicDataTradeItemVariableUnit',
  'basicDataTaxClassification',
  'basicDataPriceComparisonContentType',
  'basicDataAvailabilityDateFrom',
  'basicDataCbuIndicator',
  // Consumer Pack
  'consumerPackOrderableUnit',
  'consumerPackDespatchUnit',
  'consumerPackNetWeight',
  // Store Pack
  'storePackEANForStorePack',
  'storePackOrderableUnit',
  'storePackDespatchUnit',
  'storePackTradeItemMeasurementsLengthDepth',
  'storePackTradeItemMeasurementsWidth',
  'storePackTradeItemMeasurementsHeight',
  'storePackGrossWeight',
  // Transport Pack
  // Pallet Layer
  'palletLayerQtyOfNextLowerItem',
  'palletLayerOrderableUnit',
  'palletLayerDespatchUnit',
  'palletLayerTradeItemMeasurementsLengthDepth',
  'palletLayerTradeItemMeasurementsWidth',
  'palletLayerTradeItemMeasurementsHeight',
  'palletLayerGrossWeight',
  // Pallet
  'palletEANForStorePack',
  'palletQtyOfNextLowerItem',
  'palletOrderableUnit',
  'palletDespatchUnit',
  'palletTradeItemMeasurementsLengthDepth',
  'palletTradeItemMeasurementsWidth',
  'palletTradeItemMeasurementsHeight',
  'palletGrossWeight',
  // Price
  'priceListPriceCurrency',
  'priceListPrice',
  'priceRecommendedConsumerPrice',
  // Purchasing Data
  'purchasingDataIncoterm',
  'purchasingDataPickupLocation',
]);

export const ALL_HEADERS: IExcelHeader = {
  'Produktnamn / Beteckning': 'title',
  'Livsmedlets beteckning': 'title',
  Artikelnr: 'article',
  artikelnr: 'article',
  'artikel.nr.': 'article',
  article: 'article',
  Färg: 'color',
  Produktbeskrivning: 'short_text',
  'B-packstorlek (Kfp/Dfp)': 'consumer_size_to_order_size_ratio',
  'Förpackningsstorlek Mängd': 'package_size',
  Förpackningsstorlek: 'package_size',
  'Förpackningsstorlek Enhet': 'package_size_unit',
  Enhet: 'package_size_unit',
  Material: 'material',
  Mått: 'dimensions',
  'På produkten angiven EAN-kod': 'EAN',
  'ArtNr EAN': 'EAN',
  'EAN-kod (13 eller 8 siffror)': 'EAN',
  EAN: 'EAN',
  Volym: 'capacity',
  Varumärke: 'brand',
  Bruttovikt: 'gross_weight_num',
  'Förpackningens dimensioner - hight': 'gross_height',
  'Förpackningens dimensioner - width': 'gross_width',
  'Förpackningens dimensioner - depth': 'gross_depth',
  'Sökbegrepp och nyckelord': 'search_terms',
  Kategori: 'foodlaCategory',
  Produktnamn: 'title',
  'Pa produkten angiven EAN-kod': 'EAN',
  Ingrediensforteckning: 'ingredient_statement',
  Ingrediensförteckning: 'ingredient_statement',
  'Temp. Min': 'min_temperature',
  'Förvaringstemperatur Min': 'min_temperature',
  'Temp. Max': 'max_temperature',
  'Förvaringstemperatur Max': 'max_temperature',
  Förvaringsanvisning: 'trade_item_temperature_information',
  'Ursprungsland och härkomstplats': 'place_of_item_activity',
  'Tillverkningsland (Artikelns tillverkningsland är det land där den genomgick den sista väsentliga bearbetningen.)':
    'country_of_manufacturing_string',
  Tillverkningsland: 'country_of_manufacturing_string',
  'Nettokvantitet mängd': 'descriptive_size_amount',
  'Nettokvantitet Mängd': 'descriptive_size_amount',
  'Nettokvantitet enhet': 'descriptive_size_unit',
  'Nettokvantitet Enhet': 'descriptive_size_unit',
  'Naringsvarde per 100': 'nutritional_unit',
  'Näringsvärde per 100': 'nutritional_unit',
  'Energi kJ': 'energi_kj',
  'Energi kcal': 'energi_kcal',
  Fett: 'fett',
  'Mattat fett': 'mattatFett',
  'mättat fett': 'mattatFett',
  'enkelomättat fett': 'enkelomattatFett',
  'fleromättat fett': 'flerromattatFett',
  Kolhydrat: 'kolhydrat',
  Sockerarter: 'sockerarter',
  sockerarter: 'sockerarter',
  Fiber: 'fiber',
  Protein: 'protein',
  Salt: 'salt',
  'Bruttovikt (GRAM)': 'gross_weight_num',
  'Höjd - Förpackningens dimensioner (MM)': 'gross_height',
  'Höjd - Förpackningens dimensioner': 'gross_height',
  'Förpackningens dimensioner Höjd (MM)': 'gross_height',
  'Bredd - Förpackningens dimensioner (MM)': 'gross_width',
  'Bredd - Förpackningens dimensioner': 'gross_width',
  'Förpackningens dimensioner Bredd (MM)': 'gross_width',
  'Djup - Förpackningens dimensioner (MM)': 'gross_depth',
  'Djup - Förpackningens dimensioner': 'gross_depth',
  'Förpackningens dimensioner Djup (MM)': 'gross_depth',
  'Coop Item Categories Online': 'ItemCategoriesOnline',

  // MASTER DATA
  // Basic Data
  'Article Category (Single/Mix)': 'basicDataMixCategory',
  'Trade item variable unit': 'basicDataTradeItemVariableUnit',
  'Tax classification': 'basicDataTaxClassification',
  'Price comparison content type': 'basicDataPriceComparisonContentType',
  'Availability date from': 'basicDataAvailabilityDateFrom',
  'CBU Indicator': 'basicDataCbuIndicator',
  // Consumer Pack
  'Consumer pack- Is Orderable Unit?': 'consumerPackOrderableUnit',
  'Consumer pack- Is Despatch Unit?': 'consumerPackDespatchUnit',
  'Net weight(G)': 'consumerPackNetWeight',
  // Store Pack
  'Store pack- EAN/GTIN': 'storePackEANForStorePack',
  'Store pack- Is Orderable Unit?': 'storePackOrderableUnit',
  'Store pack- Is Despatch Unit?': 'storePackDespatchUnit',
  'Store pack- depth/Length(MM)': 'storePackTradeItemMeasurementsLengthDepth',
  'Store pack- width(MM)': 'storePackTradeItemMeasurementsWidth',
  'Store pack- height(MM)': 'storePackTradeItemMeasurementsHeight',
  'Store pack- Gross weight (G)': 'storePackGrossWeight',
  // Transport Pack
  'Transport Pack- EAN/GTIN': 'transportEANForStorePack',
  'Transport Pack- Qty of next lower item': 'transportQtyOfNextLowerItem',
  'Transport Pack- Is Orderable Unit?': 'transportOrderableUnit',
  'Transport Pack- Is Despatch Unit?': 'transportDespatchUnit',
  'Transport Pack- depth/Length(MM)': 'transportTradeItemMeasurementsLengthDepth',
  'Transport Pack- width(MM)': 'transportTradeItemMeasurementsWidth',
  'Transport Pack- height(MM)': 'transportTradeItemMeasurementsHeight',
  'Transport Pack- Gross weight (G)': 'transportGrossWeight',
  // Pallet Layer
  'Pallet Layer- EAN/GTIN': 'palletLayerEANForStorePack',
  'Pallet Layer- Qty of next lower item': 'palletLayerQtyOfNextLowerItem',
  'Pallet Layer- Is Orderable Unit?': 'palletLayerOrderableUnit',
  'Pallet Layer- Is Despatch Unit?': 'palletLayerDespatchUnit',
  'Pallet Layer- depth/Length(MM)': 'palletLayerTradeItemMeasurementsLengthDepth',
  'Pallet Layer- width(MM)': 'palletLayerTradeItemMeasurementsWidth',
  'Pallet Layer- height(MM)': 'palletLayerTradeItemMeasurementsHeight',
  'Pallet Layer- Gross weight (G)': 'palletLayerGrossWeight',
  // Pallet
  'Pallet- EAN/GTIN': 'palletEANForStorePack',
  'Pallet- Qty of next lower item': 'palletQtyOfNextLowerItem',
  'Pallet- Is Orderable Unit?': 'palletOrderableUnit',
  'Pallet- Is Despatch Unit?': 'palletDespatchUnit',
  'Pallet- depth/Length(MM)': 'palletTradeItemMeasurementsLengthDepth',
  'Pallet- width(MM)': 'palletTradeItemMeasurementsWidth',
  'Pallet- height(MM)': 'palletTradeItemMeasurementsHeight',
  'Pallet- Gross weight (G)': 'palletGrossWeight',
  // Price
  'List Price Currency': 'priceListPriceCurrency',
  'List price': 'priceListPrice',
  'Recommended consumer price(Retail price Large)': 'priceRecommendedConsumerPrice',
  // Purchasing Data
  Incoterm: 'purchasingDataIncoterm',
  'Pickup location if FCA or port if FOB, according to the logistics agreement': 'purchasingDataPickupLocation',

  'Uppfyller märkningen kraven i förordning 2013:413': 'qrLabelingSwedish',
  'Förpackning och marknadsföring fri från medicinska påståenden': 'qrMedicalClaims',
  'Förpackning och marknadsföring fri från cosmetic claims': 'qrMarketingClaimsCosmetic',
  'Innehåller kosttillskottet, i rekommenderad daglig dos, vitaminer eller mineralämnen i en daglig dos som överstiger UL-värden (tolerable upper intake levels) enligt Efsa?': 'qrEfsaUlValues',
  'Uppfyller EG 1223/2009 (kosmetikaförordningen)': 'complianceEg12232009',
  'Uppfyller solskyddsförordningens påståenden': 'complianceSunscreenRegulation',
};

export const ORDERED_FIELDS = [
  // Bold
  'foodlaCategory', // 'Kategori'
  'title', // 'Produktnamn / Beteckning'
  'article', // 'Artikelnr'
  'EAN',
  'short_text', // 'Produktbeskrivning'
  'color', // 'Färg'
  'material', // 'Material'
  'ingredient_statement', // 'Ingrediensforteckning'

  // Food
  'nutritional_unit', // 'Naringsvarde per 100'
  'energi_kj', // 'Energi kJ'
  'energi_kcal', // 'Energi kcal'
  'fett', // 'Fett'
  'mattatFett', // 'Mattat fett'
  'enkelomattatFett', // 'enkelomättat fett'
  'flerromattatFett', // 'fleromättat fett'
  'kolhydrat', // 'Kolhydrat'
  'sockerarter', // 'Sockerarter'
  'fiber', // 'Fiber'
  'protein', // 'Protein'
  'salt', // 'Salt'
  'descriptive_size_amount', // 'Nettokvantitet Mängd'
  'descriptive_size_unit', // 'Nettokvantitet Enhet'
  'min_temperature', // 'Förvaringstemperatur Min'
  'max_temperature', // 'Förvaringstemperatur Max'
  'trade_item_temperature_information', // 'Förvaringsanvisning'
  'place_of_item_activity', // 'Ursprungsland och härkomstplats'

  // Non food
  'dimensions', // 'Mått'
  'capacity', // 'Volym'
  'package_size', // 'Förpackningsstorlek Mängd'
  'package_size_unit', // 'Förpackningsstorlek Enhet'

  // Bold
  'country_of_manufacturing_string', // 'Tillverkningsland'
  'search_terms', // 'Sökbegrepp och nyckelord'

  // Non food
  'consumer_size_to_order_size_ratio', // 'B-packstorlek (Kfp/Dfp)'
  'gross_weight_num', // 'Bruttovikt (GRAM)'
  'gross_height', // 'Förpackningens dimensioner - hight'
  'gross_width', // 'Förpackningens dimensioner - width'
  'gross_depth', // 'Förpackningens dimensioner - depth'

  // Bold
  'brand', // 'Varumärke'

  // MASTER DATA ------------------------------
  // Basic Data
  'basicDataMixCategory', // 'Article Category (Single/Mix)'
  'basicDataTradeItemVariableUnit', // 'Trade item variable unit'
  'basicDataTaxClassification', // 'Tax classification'
  'basicDataPriceComparisonContentType', // 'Price comparison content type'
  'basicDataAvailabilityDateFrom', // 'Availability date from'
  'basicDataCbuIndicator', // 'CBU Indicator'
  // Consumer Pack
  'consumerPackOrderableUnit', // 'Consumer pack- Is Orderable Unit?'
  'consumerPackDespatchUnit', // 'Consumer pack- Is Despatch Unit?'
  'consumerPackNetWeight', // 'Net weight(G)'
  // Store Pack
  'storePackEANForStorePack', // 'Store pack- EAN/GTIN'
  'storePackOrderableUnit', // 'Store pack- Is Orderable Unit?'
  'storePackDespatchUnit', // 'Store pack- Is Despatch Unit?'
  'storePackTradeItemMeasurementsLengthDepth', // 'Store pack- depth/Length(MM)'
  'storePackTradeItemMeasurementsWidth', // 'Store pack- width(MM)'
  'storePackTradeItemMeasurementsHeight', // 'Store pack- height(MM)'
  'storePackGrossWeight', // 'Store pack- Gross weight (G)'
  // Transport Pack
  'transportEANForStorePack', // 'Transport Pack- EAN/GTIN'
  'transportQtyOfNextLowerItem', // 'Transport Pack- Qty of next lower item'
  'transportOrderableUnit', // 'Transport Pack- Is Orderable Unit?'
  'transportDespatchUnit', // 'Transport Pack- Is Despatch Unit?'
  'transportTradeItemMeasurementsLengthDepth', // 'Transport Pack- depth/Length(MM)'
  'transportTradeItemMeasurementsWidth', // 'Transport Pack- width(MM)'
  'transportTradeItemMeasurementsHeight', // 'Transport Pack- height(MM)'
  'transportGrossWeight', // 'Transport Pack- Gross weight (G)'
  // Pallet Layer
  'palletLayerEANForStorePack', // 'Pallet Layer- EAN/GTIN'
  'palletLayerQtyOfNextLowerItem', // 'Pallet Layer- Qty of next lower item'
  'palletLayerOrderableUnit', // 'Pallet Layer- Is Orderable Unit?'
  'palletLayerDespatchUnit', // 'Pallet Layer- Is Despatch Unit?'
  'palletLayerTradeItemMeasurementsLengthDepth', // 'Pallet Layer- depth/Length(MM)'
  'palletLayerTradeItemMeasurementsWidth', // 'Pallet Layer- width(MM)'
  'palletLayerTradeItemMeasurementsHeight', // 'Pallet Layer- height(MM)'
  'palletLayerGrossWeight', // 'Pallet Layer- Gross weight (G)'
  // Pallet
  'palletEANForStorePack', // 'Pallet- EAN/GTIN'
  'palletQtyOfNextLowerItem', // 'Pallet- Qty of next lower item'
  'palletOrderableUnit', // 'Pallet- Is Orderable Unit?'
  'palletDespatchUnit', // 'Pallet- Is Despatch Unit?'
  'palletTradeItemMeasurementsLengthDepth', // 'Pallet- depth/Length(MM)'
  'palletTradeItemMeasurementsWidth', // 'Pallet- width(MM)'
  'palletTradeItemMeasurementsHeight', // 'Pallet- height(MM)'
  'palletGrossWeight', // 'Pallet- Gross weight (G)'
  // Price
  'priceListPriceCurrency', // 'List Price Currency'
  'priceListPrice', // 'List price'
  'priceRecommendedConsumerPrice', // 'Recommended consumer price(Retail price Large)'
  // Purchasing Data
  'purchasingDataIncoterm', // 'Incoterm'
  'purchasingDataPickupLocation', // 'Pickup location if FCA or port if FOB, according to the logistics agreement'
  // ------------------------------

  // Bold
  'ItemCategoriesOnline', // 'Coop Item Categories Online'
];

export const eggWeightClass = [
  { value: 'XL', displayName: EggWeightClassEnum.XL },
  { value: 'L_XL', displayName: EggWeightClassEnum.L_XL },
  { value: 'L', displayName: EggWeightClassEnum.L },
  { value: 'M_L', displayName: EggWeightClassEnum.M_L },
  { value: 'M', displayName: EggWeightClassEnum.M },
  { value: 'S_M', displayName: EggWeightClassEnum.S_M },
  { value: 'S', displayName: EggWeightClassEnum.S },
];

export const recyclingType = [
  { value: 'BOTTLE', displayName: RecyclingTypeEnum.BOTTLE },
  { value: 'GLASS_BOTTLE', displayName: RecyclingTypeEnum.GLASS_BOTTLE },
  { value: 'RECYCLABLE_PET', displayName: RecyclingTypeEnum.RECYCLABLE_PET },
  { value: 'RECYCLABLE_PET_3_SEK', displayName: RecyclingTypeEnum.RECYCLABLE_PET_3_SEK },
  { value: 'RECYCLABLE_PET_UP_TO_1000', displayName: RecyclingTypeEnum.RECYCLABLE_PET_UP_TO_1000 },
  { value: 'RECYCLABLE_PET_UP_TO_1000_2_SEK', displayName: RecyclingTypeEnum.RECYCLABLE_PET_UP_TO_1000_2_SEK },
  { value: 'ALUMINUM_CAN', displayName: RecyclingTypeEnum.ALUMINUM_CAN },
  { value: 'ALUMINUM_CAN_2_SEK', displayName: RecyclingTypeEnum.ALUMINUM_CAN_2_SEK },
];

export const animalCategoriesForFeed = [
  { value: 'WILD_BIRD', displayName: AnimalCategoriesForFeedEnum.WILD_BIRD },
  { value: 'AMPHIBIANS', displayName: AnimalCategoriesForFeedEnum.AMPHIBIANS },
  { value: 'ANOLIS', displayName: AnimalCategoriesForFeedEnum.ANOLIS },
  { value: 'ARACHNIDS', displayName: AnimalCategoriesForFeedEnum.ARACHNIDS },
  { value: 'POGONA', displayName: AnimalCategoriesForFeedEnum.POGONA },
  { value: 'BIRDS', displayName: AnimalCategoriesForFeedEnum.BIRDS },
  { value: 'MOLLUSCS', displayName: AnimalCategoriesForFeedEnum.MOLLUSCS },
  { value: 'CAT', displayName: AnimalCategoriesForFeedEnum.CAT },
  { value: 'WHALE', displayName: AnimalCategoriesForFeedEnum.WHALE },
  { value: 'CHAMELEON', displayName: AnimalCategoriesForFeedEnum.CHAMELEON },
  { value: 'CHINCHILLA', displayName: AnimalCategoriesForFeedEnum.CHINCHILLA },
  { value: 'CRUSTACEAN', displayName: AnimalCategoriesForFeedEnum.CRUSTACEAN },
  { value: 'DOG', displayName: AnimalCategoriesForFeedEnum.DOG },
  { value: 'FISH', displayName: AnimalCategoriesForFeedEnum.FISH },
  { value: 'GECKO', displayName: AnimalCategoriesForFeedEnum.GECKO },
  { value: 'GERBIL', displayName: AnimalCategoriesForFeedEnum.GERBIL },
  { value: 'GOAT', displayName: AnimalCategoriesForFeedEnum.GOAT },
  { value: 'GUINEA_PIG', displayName: AnimalCategoriesForFeedEnum.GUINEA_PIG },
  { value: 'HAMSTER', displayName: AnimalCategoriesForFeedEnum.HAMSTER },
  { value: 'HORSE', displayName: AnimalCategoriesForFeedEnum.HORSE },
  { value: 'FERRET', displayName: AnimalCategoriesForFeedEnum.FERRET },
  { value: 'HEDGEHOG', displayName: AnimalCategoriesForFeedEnum.HEDGEHOG },
  { value: 'HUMAN', displayName: AnimalCategoriesForFeedEnum.HUMAN },
  { value: 'IGUANA', displayName: AnimalCategoriesForFeedEnum.IGUANA },
  { value: 'INSECT', displayName: AnimalCategoriesForFeedEnum.INSECT },
  { value: 'MOUSE', displayName: AnimalCategoriesForFeedEnum.MOUSE },
  { value: 'PIG', displayName: AnimalCategoriesForFeedEnum.PIG },
  { value: 'PIGEON', displayName: AnimalCategoriesForFeedEnum.PIGEON },
  { value: 'PRIMATE', displayName: AnimalCategoriesForFeedEnum.PRIMATE },
  { value: 'RABBIT', displayName: AnimalCategoriesForFeedEnum.RABBIT },
  { value: 'RAT', displayName: AnimalCategoriesForFeedEnum.RAT },
  { value: 'REPTILE', displayName: AnimalCategoriesForFeedEnum.REPTILE },
  { value: 'RODENT', displayName: AnimalCategoriesForFeedEnum.RODENT },
  { value: 'SHEEP', displayName: AnimalCategoriesForFeedEnum.SHEEP },
  { value: 'SNAIL', displayName: AnimalCategoriesForFeedEnum.SNAIL },
  { value: 'TURTLE', displayName: AnimalCategoriesForFeedEnum.TURTLE },
  { value: 'UNCLASSIFIED', displayName: AnimalCategoriesForFeedEnum.UNCLASSIFIED },
  { value: 'UNIDENTIFIED', displayName: AnimalCategoriesForFeedEnum.UNIDENTIFIED },
];

export const ironings = [
  { value: IroningEnum.IRON_LOW, displayName: IroningDisplayEnum.IRON_LOW },
  { value: IroningEnum.IRON_MEDIUM, displayName: IroningDisplayEnum.IRON_MEDIUM },
  { value: IroningEnum.IRON_HIGH, displayName: IroningDisplayEnum.IRON_HIGH },
  { value: IroningEnum.DO_NOT_IRON, displayName: IroningDisplayEnum.DO_NOT_IRON },
];

export const SALES_TYPE_LABEL = 'Säljs produkten som en styckvara eller som en viktvara?';
export const salesTypes = [
  { value: SalesTypeEnum.PIECE, displayName: 'Styckvara' },
  { value: SalesTypeEnum.WEIGHT, displayName: 'Viktvara' },
];

export const preparedForm = [
  // FC-1950: offered first so products without a prepared form can still pass the required check.
  { value: 'NOT_APPLICABLE', displayName: PreparedFormEnum.NOT_APPLICABLE },
  { value: 'FACIAL_TONER', displayName: PreparedFormEnum.FACIAL_TONER },
  { value: 'BALM', displayName: PreparedFormEnum.BALM },
  { value: 'BAR', displayName: PreparedFormEnum.BAR },
  { value: 'BREAST_TABLET', displayName: PreparedFormEnum.BREAST_TABLET },
  { value: 'DROPS', displayName: PreparedFormEnum.DROPS },
  { value: 'LIQUID', displayName: PreparedFormEnum.LIQUID },
  { value: 'GEL', displayName: PreparedFormEnum.GEL },
  { value: 'GRANULES', displayName: PreparedFormEnum.GRANULES },
  { value: 'COLLAR', displayName: PreparedFormEnum.COLLAR },
  { value: 'INHALER', displayName: PreparedFormEnum.INHALER },
  { value: 'INJECTION', displayName: PreparedFormEnum.INJECTION },
  { value: 'INSERTS_IMPLANTS', displayName: PreparedFormEnum.INSERTS_IMPLANTS },
  { value: 'WRAPPING', displayName: PreparedFormEnum.WRAPPING },
  { value: 'CAPSULE', displayName: PreparedFormEnum.CAPSULE },
  { value: 'ENEMA', displayName: PreparedFormEnum.ENEMA },
  { value: 'CREAM', displayName: PreparedFormEnum.CREAM },
  { value: 'CLYSTER', displayName: PreparedFormEnum.CLYSTER },
  { value: 'LINIMENT', displayName: PreparedFormEnum.LINIMENT },
  { value: 'LOTION', displayName: PreparedFormEnum.LOTION },
  { value: 'SOLUTION', displayName: PreparedFormEnum.SOLUTION },
  { value: 'MASK', displayName: PreparedFormEnum.MASK },
  { value: 'MICRO_CLYSTER', displayName: PreparedFormEnum.MICRO_CLYSTER },
  { value: 'MILK', displayName: PreparedFormEnum.MILK },
  { value: 'MOUSSE', displayName: PreparedFormEnum.MOUSSE },
  { value: 'ORAL_SPRAY', displayName: PreparedFormEnum.ORAL_SPRAY },
  { value: 'MOUTHWASH', displayName: PreparedFormEnum.MOUTHWASH },
  { value: 'MOUTH_SPRAY', displayName: PreparedFormEnum.MOUTH_SPRAY },
  { value: 'MOUTH_DECAY_TABLET', displayName: PreparedFormEnum.MOUTH_DECAY_TABLET },
  { value: 'NAIL_VARNISH', displayName: PreparedFormEnum.NAIL_VARNISH },
  { value: 'NASAL_DROPS', displayName: PreparedFormEnum.NASAL_DROPS },
  { value: 'NASAL_SHOWER', displayName: PreparedFormEnum.NASAL_SHOWER },
  { value: 'NASAL_RINSE', displayName: PreparedFormEnum.NASAL_RINSE },
  { value: 'NASAL_SPRAY', displayName: PreparedFormEnum.NASAL_SPRAY },
  { value: 'OIL', displayName: PreparedFormEnum.OIL },
  { value: 'PASTES', displayName: PreparedFormEnum.PASTES },
  { value: 'PLASTERS', displayName: PreparedFormEnum.PLASTERS },
  { value: 'SACHETS', displayName: PreparedFormEnum.SACHETS },
  { value: 'POWDERS', displayName: PreparedFormEnum.POWDERS },
  { value: 'POWDER', displayName: PreparedFormEnum.POWDER },
  { value: 'TRAVEL_TABLET', displayName: PreparedFormEnum.TRAVEL_TABLET },
  { value: 'ROLL_ON', displayName: PreparedFormEnum.ROLL_ON },
  { value: 'SALT', displayName: PreparedFormEnum.SALT },
  { value: 'SALVA', displayName: PreparedFormEnum.SALVA },
  { value: 'SHAMPOO', displayName: PreparedFormEnum.SHAMPOO },
  { value: 'SCRUB', displayName: PreparedFormEnum.SCRUB },
  { value: 'SERUM', displayName: PreparedFormEnum.SERUM },
  { value: 'SPRAY', displayName: PreparedFormEnum.SPRAY },
  { value: 'STICK', displayName: PreparedFormEnum.STICK },
  { value: 'SUPPOSITORIES', displayName: PreparedFormEnum.SUPPOSITORIES },
  { value: 'LOZENGE', displayName: PreparedFormEnum.LOZENGE },
  { value: 'TABLET', displayName: PreparedFormEnum.TABLET },
  { value: 'TOOTHPASTE', displayName: PreparedFormEnum.TOOTHPASTE },
  { value: 'CHEWING_CAPSULE', displayName: PreparedFormEnum.CHEWING_CAPSULE },
  { value: 'CHEWING_TABLET', displayName: PreparedFormEnum.CHEWING_TABLET },
  { value: 'CHEWING_GUM', displayName: PreparedFormEnum.CHEWING_GUM },
  { value: 'SOAP', displayName: PreparedFormEnum.SOAP },
  { value: 'VAGINAL_TABLET', displayName: PreparedFormEnum.VAGINAL_TABLET },
  { value: 'VAGITORIUM', displayName: PreparedFormEnum.VAGITORIUM },
  { value: 'WAX', displayName: PreparedFormEnum.WAX },
  { value: 'WET_WIPES', displayName: PreparedFormEnum.WET_WIPES },
  { value: 'EYE_DROPS', displayName: PreparedFormEnum.EYE_DROPS },
];

export const additionalCharacteristics = [
  { value: 'EFFECT_1_2_HOURS', displayName: AdditionalCharacteristicsEnum.EFFECT_1_2_HOURS },
  { value: 'ANTI_AGE', displayName: AdditionalCharacteristicsEnum.ANTI_AGE },
  { value: 'EFFECT_12_HOURS', displayName: AdditionalCharacteristicsEnum.EFFECT_12_HOURS },
  { value: 'EFFECT_24_HOURS', displayName: AdditionalCharacteristicsEnum.EFFECT_24_HOURS },
  { value: 'OILY_SKIN', displayName: AdditionalCharacteristicsEnum.OILY_SKIN },
  { value: 'DOG', displayName: AdditionalCharacteristicsEnum.DOG },
  { value: 'HORSE', displayName: AdditionalCharacteristicsEnum.HORSE },
  { value: 'CAT', displayName: AdditionalCharacteristicsEnum.CAT },
  { value: 'SENSITIVE_SKIN', displayName: AdditionalCharacteristicsEnum.SENSITIVE_SKIN },
  { value: 'NORMAL_MIXED_SKIN', displayName: AdditionalCharacteristicsEnum.NORMAL_MIXED_SKIN },
  { value: 'UNSCENTED', displayName: AdditionalCharacteristicsEnum.UNSCENTED },
  { value: 'PROBLEM_SKIN', displayName: AdditionalCharacteristicsEnum.PROBLEM_SKIN },
  { value: 'SILICONE_FREE', displayName: AdditionalCharacteristicsEnum.SILICONE_FREE },
  { value: 'DRY_SKIN', displayName: AdditionalCharacteristicsEnum.DRY_SKIN },
  { value: 'WWF', displayName: AdditionalCharacteristicsEnum.WWF },
  { value: 'PIGMENT_CHANGES', displayName: AdditionalCharacteristicsEnum.PIGMENT_CHANGES },
];

export const categoryWeightloss = [
  { value: 'MEAL_REPLACEMENT', displayName: CategoryWeightlossEnum.MEAL_REPLACEMENT },
  { value: 'LCD', displayName: CategoryWeightlossEnum.LCD },
  { value: 'VLCD', displayName: CategoryWeightlossEnum.VLCD },
  { value: 'NON_WEIGHT_LOSS', displayName: CategoryWeightlossEnum.NON_WEIGHT_LOSS },
];

export const approvalDietFeed = [
  { value: 'YES', displayName: ApprovalDietFeedEnum.YES },
  { value: 'NO', displayName: ApprovalDietFeedEnum.NO },
  { value: 'NOT_DIET', displayName: ApprovalDietFeedEnum.NOT_DIET },
];

export const complianceMetalsSkinContact = [
  { value: 'YES', displayName: ComplianceMetalsSkinContactEnum.YES },
  { value: 'NO', displayName: ComplianceMetalsSkinContactEnum.NO },
  { value: 'NO_DIRECT_CONTACT_WITH_SKIN', displayName: ComplianceMetalsSkinContactEnum.NO_DIRECT_CONTACT_WITH_SKIN },
];

export const confirmGuidanceSwedishElectronics = [
  { value: 'YES', displayName: ConfirmGuidanceSwedishElectronicsEnum.YES },
  { value: 'NO', displayName: ConfirmGuidanceSwedishElectronicsEnum.NO },
  { value: 'NOT_ELECTRICAL', displayName: ConfirmGuidanceSwedishElectronicsEnum.NOT_ELECTRICAL },
];

export const qrNutritionClaimsCode = [
  { value: 'YES', displayName: QrNutritionClaimsEnum.YES },
  { value: 'NO', displayName: QrNutritionClaimsEnum.NO },
  { value: 'NO_NUTRITION_CLAIMS', displayName: QrNutritionClaimsEnum.NO_NUTRITION_CLAIMS },
];

export const qrHealthClaimsCode = [
  { value: 'YES', displayName: QrHealthClaimsEnum.YES },
  { value: 'NO', displayName: QrHealthClaimsEnum.NO },
  { value: 'NO_HEALTH_CLAIMS', displayName: QrHealthClaimsEnum.NO_HEALTH_CLAIMS },
];

export const qrElectronicCode = [
  { value: 'YES', displayName: QrElectronicEnum.YES },
  { value: 'NO', displayName: QrElectronicEnum.NO },
  { value: 'NOT_ELECTRICAL', displayName: QrElectronicEnum.NOT_ELECTRICAL },
];

export const complianceToyDirective = [
  { value: 'YES', displayName: ComplianceToyDirectiveEnum.YES },
  { value: 'NO', displayName: ComplianceToyDirectiveEnum.NO },
  { value: 'NOT_TOY', displayName: ComplianceToyDirectiveEnum.NOT_TOY },
];

export const complianceBiocid = [
  { value: 'YES', displayName: ComplianceBiocidEnum.YES },
  { value: 'NO', displayName: ComplianceBiocidEnum.NO },
  { value: 'NOT_BIOCIDAL', displayName: ComplianceBiocidEnum.NOT_BIOCIDAL },
];

export const flammableSubtype = [
  { value: 'AEROSOL', displayName: FlammableSubtypeEnum.AEROSOL },
  { value: 'LIQUID_FLASHPOINT_BELOW_60C', displayName: FlammableSubtypeEnum.LIQUID_FLASHPOINT_BELOW_60C },
  { value: 'LIQUID_FLASHPOINT_ABOVE_60C', displayName: FlammableSubtypeEnum.LIQUID_FLASHPOINT_ABOVE_60C },
];

export const complianceBiocidalTreated = [
  { value: 'YES', displayName: ComplianceBiocidalTreatedEnum.YES },
  { value: 'NO', displayName: ComplianceBiocidalTreatedEnum.NO },
  { value: 'NOT_BIOCID_TREATMENT', displayName: ComplianceBiocidalTreatedEnum.NOT_BIOCID_TREATMENT },
];

export const complianceSunscreenRegulation = [
  { value: 'YES', displayName: ComplianceSunscreenRegulationEnum.YES },
  { value: 'NO', displayName: ComplianceSunscreenRegulationEnum.NO },
  { value: 'NO_SUNSCREEN', displayName: ComplianceSunscreenRegulationEnum.NO_SUNSCREEN },
];

export const sunProtectionFactor = [
  { value: 'SIX', displayName: SunProtectionFactorEnum.SIX },
  { value: 'TEN', displayName: SunProtectionFactorEnum.TEN },
  { value: 'FIFTEEN', displayName: SunProtectionFactorEnum.FIFTEEN },
  { value: 'TWENTY', displayName: SunProtectionFactorEnum.TWENTY },
  { value: 'TWENTY_FIVE', displayName: SunProtectionFactorEnum.TWENTY_FIVE },
  { value: 'THIRTY', displayName: SunProtectionFactorEnum.THIRTY },
  { value: 'FIFTY', displayName: SunProtectionFactorEnum.FIFTY },
  { value: 'FIFTY_PLUS', displayName: SunProtectionFactorEnum.FIFTY_PLUS },
];

export const fishProductionMethod = [
  { value: 'CULTIVATION_IN_WATER', displayName: FishProductionMethodEnum.CULTIVATION_IN_WATER },
  { value: 'INLAND_FISHING', displayName: FishProductionMethodEnum.INLAND_FISHING },
  { value: 'SEA_FISHING', displayName: FishProductionMethodEnum.SEA_FISHING },
];

export const fishCatchMethod = [
  { value: 'SNOOD', displayName: FishCatchMethodEnum.SNOOD },
  { value: 'VAD', displayName: FishCatchMethodEnum.VAD },
  { value: 'TRAWLS', displayName: FishCatchMethodEnum.TRAWLS },
  { value: 'DREDGING_GEAR', displayName: FishCatchMethodEnum.DREDGING_GEAR },
  { value: 'SINKERS', displayName: FishCatchMethodEnum.SINKERS },
  { value: 'ENTANGLING_NETS', displayName: FishCatchMethodEnum.ENTANGLING_NETS },
  { value: 'TRAPS', displayName: FishCatchMethodEnum.TRAPS },
  { value: 'HOOKS_AND_LINES', displayName: FishCatchMethodEnum.HOOKS_AND_LINES },
  { value: 'DREDGES_FROM_BOATS', displayName: FishCatchMethodEnum.DREDGES_FROM_BOATS },
  { value: 'HAND_HELD_DREDGES', displayName: FishCatchMethodEnum.HAND_HELD_DREDGES },
  { value: 'GEAR_FOR_CATCHING_LEAPING_FISH', displayName: FishCatchMethodEnum.GEAR_FOR_CATCHING_LEAPING_FISH },
  { value: 'CAST_NETS', displayName: FishCatchMethodEnum.CAST_NETS },
  { value: 'DROP_NETS', displayName: FishCatchMethodEnum.DROP_NETS },
  { value: 'FIXED_UNCOVERED_GILLNETS', displayName: FishCatchMethodEnum.FIXED_UNCOVERED_GILLNETS },
  { value: 'TINOR', displayName: FishCatchMethodEnum.TINOR },
  { value: 'FIXED_HAMMERS', displayName: FishCatchMethodEnum.FIXED_HAMMERS },
  { value: 'TENSION_NETS', displayName: FishCatchMethodEnum.TENSION_NETS },
  { value: 'FYKE_NETS', displayName: FishCatchMethodEnum.FYKE_NETS },
  { value: 'NETS', displayName: FishCatchMethodEnum.NETS },
  { value: 'CIRCLE_NETS', displayName: FishCatchMethodEnum.CIRCLE_NETS },
  { value: 'DRIFTING_NETS', displayName: FishCatchMethodEnum.DRIFTING_NETS },
  { value: 'FIXED_GILLNETS', displayName: FishCatchMethodEnum.FIXED_GILLNETS },
  { value: 'ANCHORED_NETS', displayName: FishCatchMethodEnum.ANCHORED_NETS },
  { value: 'COMBINED_GILL_AND_TRAMMEL_NETS', displayName: FishCatchMethodEnum.COMBINED_GILL_AND_TRAMMEL_NETS },
  { value: 'GILLNETS', displayName: FishCatchMethodEnum.GILLNETS },
  { value: 'HARPOONS', displayName: FishCatchMethodEnum.HARPOONS },
  { value: 'MECHANICAL_DREDGES', displayName: FishCatchMethodEnum.MECHANICAL_DREDGES },
  { value: 'PUMPS', displayName: FishCatchMethodEnum.PUMPS },
  { value: 'MACHINERY', displayName: FishCatchMethodEnum.MACHINERY },
  { value: 'WITHOUT_CORDAGE', displayName: FishCatchMethodEnum.WITHOUT_CORDAGE },
  { value: 'MECHANICAL_JIGGING', displayName: FishCatchMethodEnum.MECHANICAL_JIGGING },
  { value: 'HAND_OPERATED_JIGGING', displayName: FishCatchMethodEnum.HAND_OPERATED_JIGGING },
  { value: 'LONG_LINES', displayName: FishCatchMethodEnum.LONG_LINES },
  { value: 'DRIFTING_TRAPS', displayName: FishCatchMethodEnum.DRIFTING_TRAPS },
  { value: 'ANCHORED_TRAPS', displayName: FishCatchMethodEnum.ANCHORED_TRAPS },
  { value: 'CAISSONS_FROM_BOATS', displayName: FishCatchMethodEnum.CAISSONS_FROM_BOATS },
  { value: 'PORTABLE_DREDGES', displayName: FishCatchMethodEnum.PORTABLE_DREDGES },
  { value: 'SHOALS_USED_FROM_THE_SHORE', displayName: FishCatchMethodEnum.SHOALS_USED_FROM_THE_SHORE },
  { value: 'TOWLINES', displayName: FishCatchMethodEnum.TOWLINES },
  { value: 'PURSE_SEINES_AND_SINKERS', displayName: FishCatchMethodEnum.PURSE_SEINES_AND_SINKERS },
  { value: 'MISCELLANEOUS_OTHER_GEAR', displayName: FishCatchMethodEnum.MISCELLANEOUS_OTHER_GEAR },
  { value: 'UNKNOWN_GEAR', displayName: FishCatchMethodEnum.UNKNOWN_GEAR },
  { value: 'TRAWLS_USED_WITH_TRAWL_TABLES', displayName: FishCatchMethodEnum.TRAWLS_USED_WITH_TRAWL_TABLES },
  { value: 'TRAWL_WITH_TRAWL_TABLE', displayName: FishCatchMethodEnum.TRAWL_WITH_TRAWL_TABLE },
  { value: 'PELAGIC_TRAWL', displayName: FishCatchMethodEnum.PELAGIC_TRAWL },
  { value: 'DOUBLE_TRAWLS', displayName: FishCatchMethodEnum.DOUBLE_TRAWLS },
  { value: 'RINGNOT', displayName: FishCatchMethodEnum.RINGNOT },
  { value: 'PURSE_SEINE_CONTROLLED_FROM_VESSEL', displayName: FishCatchMethodEnum.PURSE_SEINE_CONTROLLED_FROM_VESSEL },
  { value: 'TWO_VESSEL_PERSE_SEINE', displayName: FishCatchMethodEnum.TWO_VESSEL_PERSE_SEINE },
  { value: 'PAIR_TRAWL', displayName: FishCatchMethodEnum.PAIR_TRAWL },
  { value: 'BOTTOM_TRAWL_FOR_PAIR_TRAWLING', displayName: FishCatchMethodEnum.BOTTOM_TRAWL_FOR_PAIR_TRAWLING },
  { value: 'PAIR_TRANSFER_TRAWL', displayName: FishCatchMethodEnum.PAIR_TRANSFER_TRAWL },
  { value: 'FISHING_GEAR_FOR_SPORT_FISHING', displayName: FishCatchMethodEnum.FISHING_GEAR_FOR_SPORT_FISHING },
  { value: 'SHORE_SEINE', displayName: FishCatchMethodEnum.SHORE_SEINE },
  { value: 'PURSE_SEINE', displayName: FishCatchMethodEnum.PURSE_SEINE },
  { value: 'DRAGGED_BY_TWO_VESSELS', displayName: FishCatchMethodEnum.DRAGGED_BY_TWO_VESSELS },
  { value: 'SCOTTISH_PURSE_SEINE', displayName: FishCatchMethodEnum.SCOTTISH_PURSE_SEINE },
  { value: 'PURSE_SEINE_DEPLOYED_FROM_BOATS', displayName: FishCatchMethodEnum.PURSE_SEINE_DEPLOYED_FROM_BOATS },
  { value: 'BOTTOM_TRAWLS', displayName: FishCatchMethodEnum.BOTTOM_TRAWLS },
  { value: 'BEAM_TRAWLS', displayName: FishCatchMethodEnum.BEAM_TRAWLS },
  { value: 'NEPHROPS_TRAWLS', displayName: FishCatchMethodEnum.NEPHROPS_TRAWLS },
  { value: 'SHRIMP_TRAWLS', displayName: FishCatchMethodEnum.SHRIMP_TRAWLS },
  { value: 'PELAGIC_TRAWLS', displayName: FishCatchMethodEnum.PELAGIC_TRAWLS },
  { value: 'SHRIMP_TRAWL', displayName: FishCatchMethodEnum.SHRIMP_TRAWL },
  { value: 'OTHER_TRAWLS', displayName: FishCatchMethodEnum.OTHER_TRAWLS },
];

export const catchArea = [
  { value: 'ARCTIC_OCEAN', displayName: CatchAreaEnum.ARCTIC_OCEAN },
  { value: 'NORTHWEST_ATLANTIC', displayName: CatchAreaEnum.NORTHWEST_ATLANTIC },
  { value: 'NORTHEAST_ATLANTIC', displayName: CatchAreaEnum.NORTHEAST_ATLANTIC },
  { value: 'BARENTS_SEA', displayName: CatchAreaEnum.BARENTS_SEA },
  { value: 'AZORES_SHELF', displayName: CatchAreaEnum.AZORES_SHELF },
  { value: 'NORTH_AZORES', displayName: CatchAreaEnum.NORTH_AZORES },
  { value: 'EAST_GREENLAND', displayName: CatchAreaEnum.EAST_GREENLAND },
  { value: 'NORTHEAST_GREENLAND', displayName: CatchAreaEnum.NORTHEAST_GREENLAND },
  { value: 'SOUTH_EAST_GREENLAND', displayName: CatchAreaEnum.SOUTH_EAST_GREENLAND },
  { value: 'NORWEGIAN_SEA_SVALBARD_BEAR', displayName: CatchAreaEnum.NORWEGIAN_SEA_SVALBARD_BEAR },
  { value: 'NORWEGIAN_SEA', displayName: CatchAreaEnum.NORWEGIAN_SEA },
  { value: 'SVALBARD_BEAR_ISLAND', displayName: CatchAreaEnum.SVALBARD_BEAR_ISLAND },
  {
    value: 'SKAGERRAK_KATTEGAT_ORESUND_BELT_BALTIC_SEA',
    displayName: CatchAreaEnum.SKAGERRAK_KATTEGAT_ORESUND_BELT_BALTIC_SEA,
  },
  { value: 'SKAGERRAK_KATTEGAT', displayName: CatchAreaEnum.SKAGERRAK_KATTEGAT },
  { value: 'ORESUND', displayName: CatchAreaEnum.ORESUND },
  { value: 'SOUND_BELT_SEA', displayName: CatchAreaEnum.SOUND_BELT_SEA },
  { value: 'BELT_SEA', displayName: CatchAreaEnum.BELT_SEA },
  { value: 'BALTIC_SEA', displayName: CatchAreaEnum.BALTIC_SEA },
  { value: 'NORTH_SEA', displayName: CatchAreaEnum.NORTH_SEA },
  { value: 'NORTHERN_NORTH_SEA', displayName: CatchAreaEnum.NORTHERN_NORTH_SEA },
  { value: 'CENTRAL_NORTH_SEA', displayName: CatchAreaEnum.CENTRAL_NORTH_SEA },
  { value: 'SOUTHERN_NORTH_SEA', displayName: CatchAreaEnum.SOUTHERN_NORTH_SEA },
  { value: 'ICELANDIC_FAROESE_SHELVES', displayName: CatchAreaEnum.ICELANDIC_FAROESE_SHELVES },
  { value: 'ICELANDIC_SHELF', displayName: CatchAreaEnum.ICELANDIC_SHELF },
  { value: 'FAROE_SHELF', displayName: CatchAreaEnum.FAROE_SHELF },
  { value: 'ROCKALL_BANK_HEBRIDEAN', displayName: CatchAreaEnum.ROCKALL_BANK_HEBRIDEAN },
  { value: 'NORTH_WEST_SCOTLAND_NORTHERN_IRELAND', displayName: CatchAreaEnum.NORTH_WEST_SCOTLAND_NORTHERN_IRELAND },
  { value: 'ROCKALL_BANK', displayName: CatchAreaEnum.ROCKALL_BANK },
  { value: 'IRISH_SEA_PORCUPINE_BANK', displayName: CatchAreaEnum.IRISH_SEA_PORCUPINE_BANK },
  { value: 'IRISH_SEA', displayName: CatchAreaEnum.IRISH_SEA },
  { value: 'WATERS_WEST_IRELAND', displayName: CatchAreaEnum.WATERS_WEST_IRELAND },
  { value: 'PORCUPINE_BANK', displayName: CatchAreaEnum.PORCUPINE_BANK },
  { value: 'EASTERN_CHANNEL', displayName: CatchAreaEnum.EASTERN_CHANNEL },
  { value: 'WESTERN_CHANNEL', displayName: CatchAreaEnum.WESTERN_CHANNEL },
  { value: 'BRISTOL_CHANNEL', displayName: CatchAreaEnum.BRISTOL_CHANNEL },
  { value: 'CELTIC_SEA_NORTH', displayName: CatchAreaEnum.CELTIC_SEA_NORTH },
  { value: 'SOUTHERN_CELTIC_SEA', displayName: CatchAreaEnum.SOUTHERN_CELTIC_SEA },
  { value: 'EASTERN_SOUTHWEST_IRELAND', displayName: CatchAreaEnum.EASTERN_SOUTHWEST_IRELAND },
  { value: 'WESTERN_SOUTHWEST_IRELAND', displayName: CatchAreaEnum.WESTERN_SOUTHWEST_IRELAND },
  { value: 'BAY_BISCAY', displayName: CatchAreaEnum.BAY_BISCAY },
  { value: 'NORTH_BAY_BISCAY', displayName: CatchAreaEnum.NORTH_BAY_BISCAY },
  { value: 'CENTRAL_BAY_BISCAY', displayName: CatchAreaEnum.CENTRAL_BAY_BISCAY },
  { value: 'SOUTHERN_BAY_BISCAY', displayName: CatchAreaEnum.SOUTHERN_BAY_BISCAY },
  { value: 'BISCAY_SEAWARD', displayName: CatchAreaEnum.BISCAY_SEAWARD },
  { value: 'WESTERN_BAY_BISCAY', displayName: CatchAreaEnum.WESTERN_BAY_BISCAY },
  { value: 'PORTUGUESE_WATERS', displayName: CatchAreaEnum.PORTUGUESE_WATERS },
  { value: 'EASTERN_PORTUGUESE_WATERS', displayName: CatchAreaEnum.EASTERN_PORTUGUESE_WATERS },
  { value: 'WESTERN_PORTUGUESE_WATERS', displayName: CatchAreaEnum.WESTERN_PORTUGUESE_WATERS },
  { value: 'WESTERN_MID_ATLANTIC', displayName: CatchAreaEnum.WESTERN_MID_ATLANTIC },
  { value: 'EASTERN_MID_ATLANTIC', displayName: CatchAreaEnum.EASTERN_MID_ATLANTIC },
  { value: 'MEDITERRANEAN_BLACK_SEA', displayName: CatchAreaEnum.MEDITERRANEAN_BLACK_SEA },
  { value: 'SOUTHWEST_ATLANTIC', displayName: CatchAreaEnum.SOUTHWEST_ATLANTIC },
  { value: 'SOUTHEAST_ATLANTIC', displayName: CatchAreaEnum.SOUTHEAST_ATLANTIC },
  { value: 'ATLANTIC_OCEAN_ANTARCTIC', displayName: CatchAreaEnum.ATLANTIC_OCEAN_ANTARCTIC },
  { value: 'WEST_INDIAN_OCEAN', displayName: CatchAreaEnum.WEST_INDIAN_OCEAN },
  { value: 'EAST_INDIAN_OCEAN', displayName: CatchAreaEnum.EAST_INDIAN_OCEAN },
  { value: 'SOUTHERN_INDIAN_OCEAN_ANTARCTICA', displayName: CatchAreaEnum.SOUTHERN_INDIAN_OCEAN_ANTARCTICA },
  { value: 'NORTHWEST_PACIFIC', displayName: CatchAreaEnum.NORTHWEST_PACIFIC },
  { value: 'NORTHEAST_PACIFIC', displayName: CatchAreaEnum.NORTHEAST_PACIFIC },
  { value: 'WESTERN_CENTRAL_PACIFIC', displayName: CatchAreaEnum.WESTERN_CENTRAL_PACIFIC },
  { value: 'EASTERN_CENTRAL_PACIFIC', displayName: CatchAreaEnum.EASTERN_CENTRAL_PACIFIC },
  { value: 'SOUTHWEST_PACIFIC', displayName: CatchAreaEnum.SOUTHWEST_PACIFIC },
  { value: 'SOUTHEAST_PACIFIC', displayName: CatchAreaEnum.SOUTHEAST_PACIFIC },
  { value: 'PACIFIC_OCEAN_ANTARCTIC', displayName: CatchAreaEnum.PACIFIC_OCEAN_ANTARCTIC },
];

interface IHazardStatementDataItem {
  value: keyof typeof HazardStatementsEnum;
  displayName: HazardStatementsEnum;
}

interface IHazardStatementGroup {
  label: string;
  listData: IHazardStatementDataItem[];
}

export const hazardStatements: IHazardStatementGroup[] = HAZARD_STATEMENT_GROUPS.map(({ label, values }) => ({
  label, listData: values.map(value => ({ value, displayName: HazardStatementsEnum[value] })),
}));

export const signalWords = [
  { value: 'WARNING', displayName: SignalWordsEnum.WARNING },
  { value: 'DANGER', displayName: SignalWordsEnum.DANGER },
  { value: 'NOT_APPLICABLE', displayName: SignalWordsEnum.NOT_APPLICABLE },
];

export const securityNotices = SECURITY_NOTICE_GROUPS.map(({ label, values }) => ({
  label, listData: values.map(value => ({ value, displayName: SecurityNoticesEnum[value] })),
}));

export const TEMPORARY_FOOD_VAT_LABEL = '6% (livsmedel, tillfälligt, tidigare 12%)';
export const TAX_CLASSIFICATION = [
  { value: 6, displayName: '6%' },
  { value: 12, displayName: '12%' },
  { value: 25, displayName: '25%' },
];

export const animalFeedTypes = [
  { value: AnimalFeedTypeEnum.COMPLETE_FEED, displayName: 'Helfoder (Foderblandning)' },
  { value: AnimalFeedTypeEnum.COMPLEMENTARY_FEED, displayName: 'Kompletteringsfoder (Foderblandning)' },
  { value: AnimalFeedTypeEnum.FEED_RAW_MATERIAL, displayName: 'Foderråvara' },
];

export const containsBatteries = [
  { value: BatteryEnum.LITHIUM, displayName: 'Ja - litiumbatterier' },
  { value: BatteryEnum.OTHER, displayName: 'Ja - övriga batterier' },
  { value: BatteryEnum.NONE, displayName: 'Nej' },
];

export const batteryTypes = [
  { value: BatteryTypeEnum.NINE_VOLT, displayName: '9V (6F22 / 6LR61)' },
  { value: BatteryTypeEnum.AA, displayName: 'AA (LR6)' },
  { value: BatteryTypeEnum.AAA, displayName: 'AAA (LR03)' },
  { value: BatteryTypeEnum.C, displayName: 'C (LR14)' },
  { value: BatteryTypeEnum.D, displayName: 'D (LR20)' },
  { value: BatteryTypeEnum.A, displayName: 'A' },
  { value: BatteryTypeEnum.B, displayName: 'B' },
  { value: BatteryTypeEnum.HR11_43, displayName: 'HR11/43' },
  { value: BatteryTypeEnum.HR15_43, displayName: 'HR15/43' },
  { value: BatteryTypeEnum.HR15_50, displayName: 'HR15/50' },
  { value: BatteryTypeEnum.HR15_51_AAUB, displayName: 'HR15/51 AAUB' },
  { value: BatteryTypeEnum.HR15_51_AAUV, displayName: 'HR15/51 AAUV' },
  { value: BatteryTypeEnum.IMR19_50, displayName: 'IMR19/50' },
  { value: BatteryTypeEnum.INR14_50, displayName: 'INR14/50' },
  { value: BatteryTypeEnum.RCRV3, displayName: 'RCRV3' },
  { value: BatteryTypeEnum.CR927, displayName: 'CR927' },
  { value: BatteryTypeEnum.CR1216, displayName: 'CR1216' },
  { value: BatteryTypeEnum.CR1225, displayName: 'CR1225' },
  { value: BatteryTypeEnum.CR1616, displayName: 'CR1616' },
  { value: BatteryTypeEnum.CR1620, displayName: 'CR1620' },
  { value: BatteryTypeEnum.CR1632, displayName: 'CR1632' },
  { value: BatteryTypeEnum.CR2012, displayName: 'CR2012' },
  { value: BatteryTypeEnum.CR2016, displayName: 'CR2016' },
  { value: BatteryTypeEnum.CR2025, displayName: 'CR2025' },
  { value: BatteryTypeEnum.CR2032, displayName: 'CR2032' },
  { value: BatteryTypeEnum.CR2354, displayName: 'CR2354' },
  { value: BatteryTypeEnum.CR2430, displayName: 'CR2430' },
  { value: BatteryTypeEnum.CR2450, displayName: 'CR2450' },
  { value: BatteryTypeEnum.CR2477, displayName: 'CR2477' },
  { value: BatteryTypeEnum.CR3032, displayName: 'CR3032' },
  { value: BatteryTypeEnum.CR123A, displayName: 'CR123A (Fotobatteri)' },
  { value: BatteryTypeEnum.CR2, displayName: 'CR2 (Fotobatteri)' },
  { value: BatteryTypeEnum.CRP2, displayName: 'CRP2' },
  { value: BatteryTypeEnum.LR44, displayName: 'LR44 / AG13 / A76' },
  { value: BatteryTypeEnum.LR54, displayName: 'LR54' },
  { value: BatteryTypeEnum.SR44, displayName: 'SR44 (357 / V357)' },
  { value: BatteryTypeEnum.SR54, displayName: 'SR54 (AG10 / 389 / 390)' },
  { value: BatteryTypeEnum.PR41, displayName: 'PR41 (312 / Brun tab)' },
  { value: BatteryTypeEnum.PR43, displayName: 'PR43' },
  { value: BatteryTypeEnum.PR44, displayName: 'PR44 (675 / Blå tab)' },
  { value: BatteryTypeEnum.PR48, displayName: 'PR48 (13 / Orange tab)' },
  { value: BatteryTypeEnum.PR70, displayName: 'PR70 (10 / Gul tab)' },
  { value: BatteryTypeEnum.OTHER, displayName: 'Övrigt' },
];

export const batteryTechnologies = [
  { value: BatteryTechnologyEnum.ALKALINE, displayName: 'Alkaliskt' },
  { value: BatteryTechnologyEnum.LITHIUM, displayName: 'Litium' },
  { value: BatteryTechnologyEnum.LITHIUM_ION, displayName: 'Litium-jon' },
  { value: BatteryTechnologyEnum.NICKEL_METAL_HYDRIDE, displayName: 'Nickel-metallhydrid' },
  { value: BatteryTechnologyEnum.CARBON_ZINC, displayName: 'Zink-kol / Brunsten' },
  { value: BatteryTechnologyEnum.ZINC_AIR, displayName: 'Zink-luft' },
  { value: BatteryTechnologyEnum.SILVER_OXIDE, displayName: 'Silveroxid' },
];

export const batteryHazardousMetals = [
  { value: BatteryHazardousMetalEnum.HG, displayName: 'Hg' },
  { value: BatteryHazardousMetalEnum.CD, displayName: 'Cd' },
  { value: BatteryHazardousMetalEnum.PB, displayName: 'Pb' },
];

// Copy from Backend: src/rest/ai/AiImageImprove/geminiModel.ts
export const GEMINI_IMPROVE_IMAGE_PROMPT = `Transform the uploaded product image into a professional e-commerce hero shot. Keep the product itself exactly as in the source — its shape, proportions, materials, colors, and every label, logo, font and character of on-pack text unchanged, sharp and fully legible across the whole product.

Place the product as the single subject, centered and straight-on at eye level, filling most of the frame, on a seamless pure white (#FFFFFF) studio background. Light it with bright, soft, even studio lighting from large diffused panels, with clean symmetric highlights and smooth catalog-grade color.

Change only the background and lighting — keep the product's existing design and packaging identical to the source.`;

export const GPT_IMAGE_2_5_IMPROVE_IMAGE_PROMPT = `Transform the uploaded product image into a professional e-commerce product cutout. Keep the product itself exactly as in the source — its shape, proportions, materials, colors, and every label, logo, font and character of on-pack text unchanged, sharp and fully legible.

Place the product as the single subject, centered and straight-on at eye level, filling most of the frame. Improve lighting and color with a clean studio look while preserving the product's existing design and packaging.

Output the product on a fully transparent background with clean alpha edges. Do not add a white or colored backdrop, checkerboard, scenery, or shadow.`;

export const GEMINI_IMPROVE_IMAGE_PROMPT_2 = `Generate the product in an environment perfectly suitable for the specific product with the goal of being displayed on the product page in an online store.

CRITICAL: Keep the complete environment/background intact. Do NOT remove or isolate the background. The product should be shown IN its environment.

Guidelines:
- Place the product in a realistic, contextually appropriate setting that enhances its appeal
- Maintain the product as the clear focal point of the image
- If product is in transparent plastic, remove the plastic
- Use appropriate lighting and composition for e-commerce display
- Ensure the environment complements but does not overshadow the product
- Create a professional, high-quality presentation suitable for online retail
- Keep the overall aesthetic clean and marketable
- The environment/background is essential - do not remove it or make it white/transparent`;
